import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Modal, ModalBody } from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { useSelector } from "react-redux"
import { Route, Routes, useNavigate, useParams } from "react-router-dom"
import Select, { components as selectComponents } from "react-select"
import ReactQuill from "react-quill"
import InfiniteScroll from "react-infinite-scroll-component"
import { Bounce, toast } from "react-toastify"
import dayjs from "dayjs"
import parse from "html-react-parser"
import DOMPurify from "dompurify"
import "react-quill/dist/quill.snow.css"
import { Avatar, AvatarGroup } from "./AvatarGroup"
import CustomButton from "../OnboardingComponents/CustomButton"
import { ICONS } from "../../constants/icons"
import { get, post, put, del } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { getUserAvatarUrl } from "../../helpers/userAvatar"
import {
  formatDisplayName,
  getUserFullName,
} from "../../helpers/userDisplay"
import { getApiErrorMessage, toastApiError } from "../../helpers/apiError"
import {
  findConversationForUser,
  findProjectMessageContextId,
} from "../../helpers/projectMessageContext"
import Skeleton from "../Common/Skeleton"
import OnlineStatus from "../OnlineStatus"
import useMessageRealtime from "../../hooks/useMessageRealtime"
import {
  getRealtimeContextId,
  unwrapRealtimePayload,
} from "../../constants/messagesRealtime"

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const ALL_RECIPIENT_VALUE = "__all__"
const ALL_RECIPIENT_OPTION = {
  value: ALL_RECIPIENT_VALUE,
  label: "All",
  isAll: true,
}

const ALLOWED_MESSAGE_MEDIA_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "application/pdf",
  "text/csv",
  "video/mp4",
  "video/quicktime",
  "video/x-msvideo",
  "video/x-ms-wmv",
]

const MESSAGE_MEDIA_ACCEPT = ALLOWED_MESSAGE_MEDIA_TYPES.join(",")

/** User-facing allowed formats (tooltip + validation). */
const MESSAGE_MEDIA_TYPES_HINT =
  "JPEG, PNG, WebP, GIF, PDF, CSV, MP4, MOV, AVI, or WMV"

const MESSAGE_MEDIA_UNSUPPORTED_MSG = `Unsupported file type. Please use ${MESSAGE_MEDIA_TYPES_HINT}.`

const MESSAGE_MEDIA_ATTACH_TOOLTIP = `Attach a file (${MESSAGE_MEDIA_TYPES_HINT})`

const mediaTypeFromMime = mimeType => {
  const mime = String(mimeType || "").toLowerCase()
  if (mime.startsWith("image/")) return "image"
  if (mime.startsWith("video/")) return "video"
  if (mime === "application/pdf") return "document"
  if (mime === "text/csv") return "document"
  return "file"
}

const normalizeUploadedMedias = result => {
  if (Array.isArray(result)) return result
  if (Array.isArray(result?.data)) return result.data
  if (Array.isArray(result?.medias)) return result.medias
  if (result && typeof result === "object" && result.url) return [result]
  return []
}

const mapUploadedMedia = (item, fallbackFile) => {
  const mimeType = item?.mime_type || item?.mimeType || fallbackFile?.type || ""
  return {
    temp_media_id:
      item?.temp_media_id || item?.tempMediaId || item?.id || "",
    url: item?.url || "",
    path: item?.path || item?.original_path || "",
    disk: item?.disk || "s3",
    mime_type: mimeType,
    type: item?.type || mediaTypeFromMime(mimeType),
    size: Number(item?.size ?? fallbackFile?.size ?? 0) || 0,
    name: item?.name || fallbackFile?.name || "Attachment",
  }
}

const toMessageMediaPayload = medias =>
  (Array.isArray(medias) ? medias : [])
    .filter(item => item && !item.isUploading && item.url && item.path)
    .map(({ temp_media_id, url, path, disk, mime_type, type, size }) => ({
      ...(temp_media_id ? { temp_media_id } : {}),
      url,
      path,
      disk,
      mime_type,
      type,
      size,
    }))

const buildUpdateMediasPayload = message => {
  const fromRaw = Array.isArray(message?.medias) ? message.medias : []
  if (fromRaw.length > 0) {
    return fromRaw
      .filter(Boolean)
      .map(item => {
        const mime = item.mime_type || item.mimeType || item.mime || ""
        const typeRaw = String(item.type || "").toLowerCase()
        return {
          url: item.url || "",
          path: item.path || item.original_path || "",
          disk: item.disk || "s3",
          mime_type: mime,
          type:
            typeRaw === "image" || typeRaw === "video" || typeRaw === "document"
              ? typeRaw
              : mediaTypeFromMime(mime),
          size: Number(item.size) || 0,
        }
      })
      .filter(item => item.url || item.path)
  }

  return (Array.isArray(message?.attachments) ? message.attachments : [])
    .filter(item => item?.url || item?.path)
    .map(item => ({
      url: item.url || "",
      path: item.path || "",
      disk: item.disk || "s3",
      mime_type: item.mime || item.mime_type || "",
      type: item.isImage
        ? "image"
        : item.isVideo
          ? "video"
          : mediaTypeFromMime(item.mime || item.mime_type || ""),
      size: Number(item.rawSize) || 0,
    }))
}

const revokeMediaPreview = media => {
  if (media?.previewUrl) URL.revokeObjectURL(media.previewUrl)
}

const createLocalMediaPreviews = files =>
  files.map((file, index) => {
    const mimeType = file.type || ""
    const type = mediaTypeFromMime(mimeType)
    const previewUrl = URL.createObjectURL(file)
    return {
      localId: `${file.name}-${file.lastModified}-${file.size}-${index}-${Date.now()}`,
      previewUrl,
      url: type === "image" ? previewUrl : "",
      path: "",
      disk: "s3",
      mime_type: mimeType,
      type,
      size: Number(file.size) || 0,
      name: file.name || "Attachment",
      isUploading: true,
    }
  })

const settleMediaPreviews = (items, previews, uploaded) => {
  const previewIds = new Set(previews.map(item => item.localId))
  return items.flatMap(item => {
    if (!previewIds.has(item.localId)) return [item]
    const index = previews.findIndex(
      preview => preview.localId === item.localId,
    )
    const remote = uploaded[index]
    revokeMediaPreview(item)
    if (!remote) return []
    return [{ ...remote, localId: item.localId, isUploading: false }]
  })
}

const MessageUploadThumb = ({ media, fallback }) => {
  const previewSrc = media?.previewUrl || media?.url || ""
  const showImage = media?.type === "image" && previewSrc

  return (
    <span
      className={`project-messages__upload-thumb${
        media?.isUploading ? " project-messages__upload-thumb--uploading" : ""
      }`}
    >
      {showImage ? (
        <img src={previewSrc} alt="" />
      ) : (
        <span className="project-messages__upload-thumb-fallback">
          {fallback}
        </span>
      )}
      {media?.isUploading ? (
        <span className="project-messages__upload-overlay" aria-hidden="true">
          <span className="project-messages__upload-spinner" />
        </span>
      ) : null}
    </span>
  )
}

const uploadMessageMediaFiles = async files => {
  const formData = new FormData()
  files.forEach(file => {
    formData.append("files[]", file)
  })

  const response = await post(endpoints.uploadMessageMedias, formData, {
    skipErrorToast: true,
  })

  if (!response?.data?.success) {
    const error = new Error("Failed to upload media")
    error.response = { data: response?.data }
    error.payload = response?.data || response
    throw error
  }

  const uploaded = normalizeUploadedMedias(response.data.result).map(
    (item, index) => mapUploadedMedia(item, files[index]),
  )

  if (uploaded.length === 0) {
    throw new Error("No media returned from upload")
  }

  return {
    uploaded,
    message: response?.data?.message || "Media uploaded successfully.",
  }
}

const stripHtml = value =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()

const getProjectInitials = (name = "") =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word.charAt(0))
    .join("")
    .toUpperCase() || "—"

const newMessageSchema = Yup.object({
  subject: Yup.string().trim().required("Subject is required"),
  body: Yup.string().test(
    "message-required",
    "Message is required",
    value => stripHtml(value).length > 0,
  ),
})

const QUILL_MODULES = {
  toolbar: false,
}

const QUILL_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "link",
  "image",
]

const MESSAGE_TOOLBAR = [
  { id: "insert", src: ICONS.messageToolbarPlus, label: "Insert link" },
  {
    id: "attach",
    src: ICONS.messageToolbarType,
    label: MESSAGE_MEDIA_ATTACH_TOOLTIP,
  },
  { id: "emoji", src: ICONS.messageToolbarSmile, label: "Emoji" },
  { id: "mention", src: ICONS.messageToolbarAt, label: "Mention" },
  { id: "format", src: ICONS.messageToolbarPaperclip, label: "Format" },
]

const FORMAT_OPTIONS = [
  { id: "bold", label: "Bold", format: "bold" },
  { id: "italic", label: "Italic", format: "italic" },
  { id: "underline", label: "Underline", format: "underline" },
  { id: "strike", label: "Strike", format: "strike" },
  { id: "ordered", label: "Numbered list", format: "list", value: "ordered" },
  { id: "bullet", label: "Bullet list", format: "list", value: "bullet" },
]

const SEARCH_DEBOUNCE_MS = 300
const THREAD_SCROLL_ID = "project-messages-thread"
const THREAD_NEAR_BOTTOM_PX = 96
const THREAD_TOP_LOAD_PX = 120
const THREAD_OLDER_SCROLL_THRESHOLD = "120px"

const formatPersonName = (user, fallback = "") => {
  if (!user) return fallback
  if (typeof user === "string") {
    const name = formatDisplayName(user.trim())
    return name && name.toLowerCase() !== "unknown" ? name : fallback
  }
  const name = getUserFullName(user)
  if (!name || name.toLowerCase() === "unknown") return fallback
  return name
}

const isUsableDisplayName = value => {
  const name = String(value || "").trim()
  if (!name) return false
  const lower = name.toLowerCase()
  return (
    lower !== "unknown" &&
    lower !== "someone" &&
    lower !== "collaborator"
  )
}

const isContactUpdatePayload = payload => {
  if (!payload || typeof payload !== "object") return false
  if ("contact" in payload) return true
  const unwrapped = unwrapRealtimePayload(payload)
  if (!unwrapped || typeof unwrapped !== "object") return false
  if ("contact" in unwrapped) return true
  const hasBody =
    unwrapped.message != null ||
    unwrapped.body != null ||
    unwrapped.content != null
  const hasMedia =
    (Array.isArray(unwrapped.medias) && unwrapped.medias.length > 0) ||
    (Array.isArray(unwrapped.media) && unwrapped.media.length > 0) ||
    (Array.isArray(unwrapped.attachments) && unwrapped.attachments.length > 0)
  if (hasBody || hasMedia) return false
  return Boolean(
    unwrapped.last_message_sent_at ||
      unwrapped.last_message ||
      unwrapped.last_message_sent ||
      unwrapped.collaborators ||
      unwrapped.participants ||
      unwrapped.subject != null,
  )
}

const isValidThreadMessage = message => {
  if (!message?.id) return false
  const id = String(message.id)
  if (id.startsWith("contact-preview-")) return false
  const hasText = Boolean(String(message.text || "").trim())
  const hasHtml = Boolean(stripHtml(message.html || ""))
  const hasAttachments = (message.attachments?.length || 0) > 0
  return hasText || hasHtml || hasAttachments
}

const resolveParticipantName = (participants, senderId, fallback = "") => {
  if (senderId == null || senderId === "") return fallback
  const match = (Array.isArray(participants) ? participants : []).find(
    person => String(person?.id) === String(senderId),
  )
  if (!match) return fallback
  return formatPersonName(match) || match.name || fallback
}

/** Context collaborators may be flat users or `{ user: {...} }` rows. */
const normalizePerson = person => {
  if (!person || typeof person !== "object") return person || null
  const nested =
    (person.user && typeof person.user === "object" && person.user) ||
    (person.member && typeof person.member === "object" && person.member) ||
    (person.invitee && typeof person.invitee === "object" && person.invitee) ||
    null
  if (!nested) return person
  return {
    ...person,
    ...nested,
    id: nested.id || person.user_id || person.id,
    profile_media: nested.profile_media || person.profile_media,
    image: nested.image || person.image,
    avatar: nested.avatar || person.avatar,
  }
}

const mapPersonToParticipant = person => {
  const user = normalizePerson(person) || {}
  return {
    id: user.id,
    name: formatPersonName(user),
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    thumbnail: user.thumbnail,
    image: user.image,
    profile_media: user.profile_media,
    avatar: getUserAvatarUrl(user),
  }
}

const resolveMessageSenderName = (message, participants = []) => {
  const fromParticipants = resolveParticipantName(
    participants,
    message?.senderId,
  )
  if (isUsableDisplayName(fromParticipants)) return fromParticipants
  if (isUsableDisplayName(message?.sender)) return message.sender
  return formatPersonName(message?.sender) || ""
}

const getThreadParticipants = (selected, project) => {
  if (Array.isArray(selected?.participants) && selected.participants.length) {
    return selected.participants
  }
  return (Array.isArray(project?.collaborators) ? project.collaborators : []).map(
    mapPersonToParticipant,
  )
}

const enrichThreadMessage = (
  message,
  { participants = [], currentUser = null } = {},
) => {
  if (!message) return message

  const currentUserId =
    currentUser?.id != null ? String(currentUser.id) : ""
  const isOwn =
    Boolean(message.isMe) ||
    (currentUserId &&
      message.senderId &&
      String(message.senderId) === currentUserId)
  const selfName = formatPersonName(currentUser) || "You"

  let sender = resolveMessageSenderName(message, participants)
  if (!isUsableDisplayName(sender) && isOwn) {
    sender = selfName
  }
  if (!isUsableDisplayName(sender) && !isOwn && message.senderId) {
    const others = participants.filter(
      person =>
        !currentUserId || String(person?.id) !== currentUserId,
    )
    const fallbackPerson = participants.find(
      person => String(person?.id) === String(message.senderId),
    ) || (others.length === 1 ? others[0] : null)
    sender =
      formatPersonName(fallbackPerson) || fallbackPerson?.name || sender
  }

  const participantMatch = participants.find(
    person =>
      message.senderId && String(person?.id) === String(message.senderId),
  )

  return {
    ...message,
    sender: isUsableDisplayName(sender)
      ? sender
      : isOwn
        ? selfName
        : sender,
    isMe: isOwn,
    avatar:
      message.avatar ||
      getUserAvatarUrl(participantMatch) ||
      (isOwn ? getUserAvatarUrl(currentUser) : null),
  }
}

const mapThreadMessagesFromApi = (
  rows,
  { participants = [], currentUser = null, currentUserId = "" } = {},
) =>
  (Array.isArray(rows) ? rows : []).map(message =>
    enrichThreadMessage(
      mapApiMessage(message, currentUserId, currentUser),
      { participants, currentUser },
    ),
  )

const formatThreadTime = value => {
  if (!value) return ""
  const date = dayjs(value)
  if (!date.isValid()) return String(value)
  if (date.isSame(dayjs(), "day")) return date.format("h:mm A")
  if (date.isSame(dayjs(), "year")) return date.format("MMM D")
  return date.format("MMM D, YYYY")
}

const looksLikeHtml = value => /<\/?[a-z][\s\S]*>/i.test(String(value || ""))

const formatFileSize = bytes => {
  const size = Number(bytes)
  if (!Number.isFinite(size) || size <= 0) return ""
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024)
    return `${(size / 1024).toFixed(size < 10 * 1024 ? 1 : 0)} KB`
  return `${(size / (1024 * 1024)).toFixed(size < 10 * 1024 * 1024 ? 1 : 0)} MB`
}

const resolveMessageMedias = message => {
  if (Array.isArray(message?.medias)) return message.medias
  if (Array.isArray(message?.media)) return message.media
  if (Array.isArray(message?.attachments)) return message.attachments
  if (message?.media && typeof message.media === "object")
    return [message.media]
  if (message?.attachment && typeof message.attachment === "object") {
    return [message.attachment]
  }
  return []
}

const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|avif|heic|bmp)(\?|#|$)/i
const VIDEO_EXT_RE = /\.(mp4|mov|webm|avi|wmv|m4v|mkv)(\?|#|$)/i

const mapApiAttachment = media => {
  if (!media) return null
  const url = media.url || media.previewUrl || ""
  const name =
    media.name ||
    media.file_name ||
    String(media.path || url || "")
      .split("/")
      .pop() ||
    "Attachment"
  const mime = String(media.mime_type || media.mimeType || "").toLowerCase()
  const typeRaw = String(media.type || "").toLowerCase()
  const haystack = `${name} ${url} ${media.path || ""}`
  const isVideo =
    media.isVideo === true ||
    typeRaw === "video" ||
    mime.startsWith("video/") ||
    VIDEO_EXT_RE.test(haystack)
  const isImage =
    media.isImage === true ||
    (!isVideo &&
      (typeRaw === "image" ||
        mime.startsWith("image/") ||
        IMAGE_EXT_RE.test(haystack)))
  const type = isImage
    ? "IMAGE"
    : isVideo
      ? "VIDEO"
      : typeRaw === "document" || mime.includes("pdf")
        ? "PDF"
        : String(media.type || "FILE").toUpperCase()

  return {
    id: media.id,
    name,
    size: media.size_label || formatFileSize(media.size),
    rawSize: Number(media.size) || 0,
    path: media.path || media.original_path || "",
    disk: media.disk || "s3",
    type,
    url,
    isImage,
    isVideo,
    mime,
  }
}

const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢"]

const normalizeEmoji = value => String(value || "").replace(/\uFE0F/g, "")

const normalizeReactionItem = item => {
  if (!item) return null
  if (typeof item === "string") {
    const emoji = item
    return emoji ? { emoji, count: 1 } : null
  }
  const emoji = item.emoji || item.reaction || item.type || ""
  if (!emoji) return null
  const count = Number(item.count ?? item.total ?? 0)
  if (!Number.isFinite(count) || count <= 0) return null
  return { emoji, count }
}

const mapReactionSummary = summary => {
  if (!Array.isArray(summary)) return null
  const merged = new Map()
  summary.map(normalizeReactionItem).filter(Boolean).forEach(item => {
    const key = normalizeEmoji(item.emoji)
    const previous = merged.get(key)
    if (previous) {
      merged.set(key, {
        emoji: previous.emoji,
        count: previous.count + item.count,
      })
    } else {
      merged.set(key, item)
    }
  })
  return Array.from(merged.values())
}

const mapMessageReactions = message => {
  const source =
    Array.isArray(message?.reactions) && message.reactions.length > 0
      ? message.reactions
      : Array.isArray(message?.reaction_summary)
        ? message.reaction_summary
        : []

  return mapReactionSummary(source) || []
}

const getMyReaction = message => {
  const value =
    message?.my_reaction ||
    message?.user_reaction ||
    message?.current_user_reaction ||
    message?.reacted ||
    ""
  if (typeof value === "string") return value
  return value?.emoji || value?.reaction || ""
}

const replaceReactionCounts = (reactions, previousReaction, nextReaction) => {
  const next = (Array.isArray(reactions) ? reactions : []).map(item => ({
    ...item,
  }))

  if (previousReaction && previousReaction !== nextReaction) {
    const previousIndex = next.findIndex(
      item => normalizeEmoji(item.emoji) === normalizeEmoji(previousReaction),
    )
    if (previousIndex >= 0) {
      const remaining = Number(next[previousIndex].count || 0) - 1
      if (remaining > 0) {
        next[previousIndex] = { ...next[previousIndex], count: remaining }
      } else {
        next.splice(previousIndex, 1)
      }
    }
  }

  if (nextReaction && previousReaction !== nextReaction) {
    const nextIndex = next.findIndex(
      item => normalizeEmoji(item.emoji) === normalizeEmoji(nextReaction),
    )
    if (nextIndex >= 0) {
      next[nextIndex] = {
        ...next[nextIndex],
        count: Number(next[nextIndex].count || 0) + 1,
      }
    } else {
      next.push({ emoji: nextReaction, count: 1 })
    }
  }

  return next
}

const resolveSenderUser = (raw, currentUser) => {
  let senderUser = raw?.sender || raw?.user || {}
  const senderId =
    senderUser?.id != null
      ? String(senderUser.id)
      : raw?.sender_id != null
        ? String(raw.sender_id)
        : ""
  const currentId = currentUser?.id != null ? String(currentUser.id) : ""
  const isSelf = Boolean(currentId && senderId && senderId === currentId)

  // API create/update often returns a thin sender (id only). Fill name + avatar
  // from the logged-in user so the bubble never shows "Unknown".
  if (isSelf && currentUser) {
    const hasName = Boolean(formatPersonName(senderUser))
    const hasAvatar = Boolean(getUserAvatarUrl(senderUser))
    if (!hasName || !hasAvatar) {
      senderUser = {
        ...currentUser,
        ...senderUser,
        first_name: senderUser.first_name || currentUser.first_name,
        last_name: senderUser.last_name || currentUser.last_name,
        name: senderUser.name || currentUser.name,
        full_name: senderUser.full_name || currentUser.full_name,
        email: senderUser.email || currentUser.email,
        profile_media: currentUser.profile_media || senderUser.profile_media,
        image: senderUser.image || currentUser.image,
        avatar: senderUser.avatar || currentUser.avatar,
      }
    }
  }

  return { senderUser, senderId, isSelf }
}

const mapApiMessage = (message, currentUserId, currentUser) => {
  const raw = unwrapRealtimePayload(message) || message
  const { senderUser, senderId, isSelf } = resolveSenderUser(raw, currentUser)
  const senderName =
    formatPersonName(senderUser) ||
    formatPersonName(raw?.sender_name) ||
    (isSelf ? formatPersonName(currentUser) : "") ||
    (isSelf ? "You" : "")
  const bodyHtml = raw?.message || raw?.body || raw?.content || ""
  const plain = stripHtml(bodyHtml)
  const medias = resolveMessageMedias(raw)
  const attachments = medias.map(mapApiAttachment).filter(Boolean)
  const sentAt = raw?.sent_at || raw?.created_at || raw?.last_updated_at || ""

  return {
    id: raw?.id || raw?.uuid,
    senderId,
    sender: senderName,
    avatar:
      getUserAvatarUrl(senderUser) ||
      (senderId && currentUserId && senderId === String(currentUserId)
        ? getUserAvatarUrl(currentUser)
        : null),
    time: formatThreadTime(sentAt),
    role: raw?.role || senderUser?.role || "",
    text: plain,
    html: looksLikeHtml(bodyHtml) ? bodyHtml : "",
    paragraphs: [],
    signOff: raw?.sign_off || "",
    signatureName: raw?.signature_name || "",
    signatureTitle: raw?.signature_title || "",
    attachment: attachments[0] || null,
    attachments,
    medias,
    reactions: mapMessageReactions(raw),
    myReaction: getMyReaction(raw),
    repliesCount: Number(raw?.replies_count) || 0,
    parentMessageId: raw?.parent_message_id || null,
    sentAt,
    contextId: raw?.context_id || raw?.message_context_id || "",
    isMe: Boolean(
      currentUserId && senderId && String(currentUserId) === senderId,
    ),
  }
}

const getMessageTimestamp = message => {
  const date = dayjs(message?.sentAt)
  return date.isValid() ? date.valueOf() : 0
}

const isLocalMessageId = id => String(id || "").startsWith("local-")

const isOptimisticMatch = (local, incoming) => {
  if (!local || !incoming || !isLocalMessageId(local.id)) return false
  if (!local.isMe || !incoming.isMe) return false
  if ((local.text || "") !== (incoming.text || "")) return false
  const localCount = local.attachments?.length || 0
  const incomingCount = incoming.attachments?.length || 0
  if (localCount !== incomingCount) return false
  const delta = Math.abs(
    getMessageTimestamp(local) - getMessageTimestamp(incoming),
  )
  return delta <= 120000
}

const mergeThreadMessages = (existing, incoming) => {
  const list = []
  ;(Array.isArray(existing) ? existing : []).forEach(item => {
    if (item?.id == null) return
    list.push(item)
  })

  ;(Array.isArray(incoming) ? incoming : []).forEach(item => {
    if (item?.id == null || !isValidThreadMessage(item)) return
    const id = String(item.id)
    const existingIndex = list.findIndex(row => String(row.id) === id)
    if (existingIndex >= 0) {
      const prev = list[existingIndex]
      list[existingIndex] = {
        ...prev,
        ...item,
        sender: isUsableDisplayName(item.sender)
          ? item.sender
          : prev.sender,
        avatar: item.avatar || prev.avatar,
        medias:
          Array.isArray(item.medias) && item.medias.length > 0
            ? item.medias
            : prev.medias,
        attachments:
          Array.isArray(item.attachments) && item.attachments.length > 0
            ? item.attachments
            : prev.attachments,
      }
      return
    }
    if (!isLocalMessageId(id)) {
      const localIndex = list.findIndex(row => isOptimisticMatch(row, item))
      if (localIndex >= 0) {
        const prev = list[localIndex]
        list[localIndex] = {
          ...item,
          sender: isUsableDisplayName(item.sender)
            ? item.sender
            : prev.sender,
          avatar: item.avatar || prev.avatar,
          medias:
            Array.isArray(item.medias) && item.medias.length > 0
              ? item.medias
              : prev.medias,
          attachments:
            Array.isArray(item.attachments) && item.attachments.length > 0
              ? item.attachments
              : prev.attachments,
        }
        return
      }
    }
    list.push(item)
  })

  return list.sort((a, b) => {
    const diff = getMessageTimestamp(a) - getMessageTimestamp(b)
    if (diff !== 0) return diff
    return String(a.id).localeCompare(String(b.id))
  })
}

const parseThreadPage = (result, fallbackPage = 1) => {
  const currentPage = Number(result?.current_page) || fallbackPage
  const lastPage = Number(result?.last_page) || 1
  return {
    currentPage,
    lastPage,
    hasMoreOlder: currentPage < lastPage,
  }
}

const extractCreatedMessage = payload => {
  if (!payload || typeof payload !== "object") return null
  if (Array.isArray(payload)) {
    return payload.find(item => item && item.id != null) || null
  }

  const nested = unwrapRealtimePayload(payload)
  if (nested && nested !== payload) {
    const fromNested = extractCreatedMessage(nested)
    if (fromNested) return fromNested
  }

  const looksLikeMessage =
    payload.id != null &&
    (payload.message != null ||
      payload.body != null ||
      payload.content != null ||
      payload.sender ||
      payload.user ||
      payload.medias ||
      payload.media ||
      payload.attachments ||
      payload.created_at ||
      payload.sent_at)

  if (looksLikeMessage) return payload
  if (payload.result) return extractCreatedMessage(payload.result)
  if (payload.data) return extractCreatedMessage(payload.data)
  return null
}

const mapDirectContactPreview = payload => {
  const data = unwrapRealtimePayload(payload) || {}
  const contact =
    data.contact ||
    payload?.contact ||
    (data.id || data.context_id || data.conversation_id ? data : null)
  if (!contact || typeof contact !== "object") return null

  const contextId =
    contact.id || contact.context_id || contact.conversation_id || ""
  if (!contextId) return null

  const lastMessage =
    contact.last_message ||
    contact.last_message_sent ||
    data.last_message ||
    {}
  const sender = lastMessage.sender || lastMessage.user || null
  const senderId =
    sender?.id != null
      ? String(sender.id)
      : lastMessage.sender_id != null
        ? String(lastMessage.sender_id)
        : ""
  const collaborators = Array.isArray(contact.collaborators)
    ? contact.collaborators.map(normalizePerson)
    : Array.isArray(contact.participants)
      ? contact.participants.map(normalizePerson)
      : []
  const senderName =
    formatPersonName(normalizePerson(sender)) ||
    resolveParticipantName(collaborators, senderId) ||
    null

  return {
    contextId,
    id: lastMessage.id || `contact-preview-${contextId}`,
    text: stripHtml(lastMessage.body || lastMessage.message || ""),
    time: formatThreadTime(
      contact.last_message_sent_at ||
        lastMessage.sent_at ||
        lastMessage.created_at,
    ),
    sender: isUsableDisplayName(senderName) ? senderName : null,
    senderId,
    sentAt:
      contact.last_message_sent_at ||
      lastMessage.sent_at ||
      lastMessage.created_at ||
      "",
  }
}

const mapRealtimeMessage = (payload, currentUserId, currentUser) => {
  if (isContactUpdatePayload(payload)) return null
  const raw = unwrapRealtimePayload(payload)
  if (!raw || typeof raw !== "object") return null
  if (raw.contact && typeof raw.contact === "object") return null
  const mapped = mapApiMessage(raw, currentUserId, currentUser)
  if (!mapped?.id || !isValidThreadMessage(mapped)) return null
  return {
    ...mapped,
    contextId:
      mapped.contextId || getRealtimeContextId(payload) || raw.context_id || "",
  }
}

const buildLocalOutgoingMessage = ({ currentUser, text, medias }) => {
  const sentAt = new Date().toISOString()
  const attachments = (medias || []).map(mapApiAttachment).filter(Boolean)
  const senderName = formatPersonName(currentUser) || "You"

  return {
    id: `local-${Date.now()}`,
    senderId: currentUser?.id != null ? String(currentUser.id) : "",
    sender: senderName,
    avatar: getUserAvatarUrl(currentUser),
    time: formatThreadTime(sentAt),
    role: currentUser?.role || "",
    text: stripHtml(text),
    html: looksLikeHtml(text) ? text : "",
    paragraphs: [],
    signOff: "",
    signatureName: "",
    signatureTitle: "",
    attachment: attachments[0] || null,
    attachments,
    reactions: [],
    myReaction: null,
    repliesCount: 0,
    sentAt,
    isMe: true,
  }
}

const ThreadOlderLoader = () => (
  <div
    className="project-messages__thread-loader"
    aria-busy="true"
    aria-live="polite"
  >
    <Skeleton width="42%" height={8} />
    <Skeleton width="28%" height={8} />
  </div>
)

const sanitizeMessageHtml = html =>
  DOMPurify.sanitize(String(html || ""), { USE_PROFILES: { html: true } })

const renderMessageHtml = html => parse(sanitizeMessageHtml(html))

const extractContextMessages = (item, currentUserId, currentUser) => {
  const rows = Array.isArray(item?.messages)
    ? item.messages
    : Array.isArray(item?.latest_messages)
      ? item.latest_messages
      : Array.isArray(item?.thread)
        ? item.thread
        : item?.last_message_sent
          ? [item.last_message_sent]
          : item?.last_message
            ? [item.last_message]
            : []

  return rows.map(message =>
    mapApiMessage(message, currentUserId, currentUser),
  )
}

const mapContextToConversation = (item, currentUserId, currentUser) => {
  const collaborators = Array.isArray(item?.collaborators)
    ? item.collaborators
    : []
  const participants = collaborators.map(mapPersonToParticipant)
  const otherParticipants = participants.filter(
    person =>
      !currentUserId || String(person.id) !== String(currentUserId),
  )

  const isGroup = collaborators.length > 1
  const lastMessageSender =
    normalizePerson(item?.last_message_sent?.sender) ||
    normalizePerson(item?.last_message?.sender)
  const title =
    (otherParticipants.length === 1
      ? formatPersonName(otherParticipants[0]) || otherParticipants[0]?.name
      : "") ||
    (participants.length === 1
      ? formatPersonName(participants[0]) || participants[0]?.name
      : "") ||
    formatPersonName(lastMessageSender) ||
    item?.subject ||
    "Conversation"
  const messages = extractContextMessages(item, currentUserId, currentUser).map(
    message => ({
      ...message,
      sender:
        resolveMessageSenderName(message, participants) ||
        (isUsableDisplayName(message.sender) ? message.sender : ""),
    }),
  )
  const latestMessage = messages[messages.length - 1]
  const previewSender =
    resolveMessageSenderName(latestMessage, participants) || null
  const previewText =
    item?.last_message_preview ||
    item?.last_message_sent?.message ||
    item?.last_message_sent?.body ||
    item?.last_message?.message ||
    item?.last_message?.body ||
    latestMessage?.text ||
    item?.subject ||
    ""

  const rawCreator =
    item?.created_by_user ||
    item?.creator ||
    item?.user ||
    item?.owner ||
    null
  const createdBy = rawCreator ? normalizePerson(rawCreator) : null
  const createdByName = createdBy
    ? formatPersonName(createdBy) || createdBy?.name || ""
    : ""

  return {
    id: item.id,
    title,
    isGroup,
    // Project threads are private to the project team (Figma chat header meta).
    isPrivate: true,
    contextType: item?.context_type || "",
    subject: item?.subject || "",
    createdByName,
    createdAt: item?.created_at || "",
    participants,
    lastMessage: {
      text: stripHtml(previewText),
      time: formatThreadTime(
        item?.last_message_sent_at ||
          item?.last_message_sent?.sent_at ||
          item?.last_message?.created_at ||
          item?.created_at,
      ),
      sender: isUsableDisplayName(previewSender) ? previewSender : null,
    },
    messages,
    raw: item,
  }
}

const EMOJI_CATEGORIES = [
  {
    id: "smileys",
    label: "Smileys",
    emojis: [
      "😀",
      "😃",
      "😄",
      "😁",
      "😆",
      "😅",
      "😂",
      "🤣",
      "😊",
      "😇",
      "🙂",
      "😉",
      "😌",
      "😍",
      "🥰",
      "😘",
      "😗",
      "😙",
      "😚",
      "😋",
      "😛",
      "😝",
      "😜",
      "🤪",
      "🤨",
      "🧐",
      "🤓",
      "😎",
      "🤩",
      "🥳",
      "😏",
      "😒",
      "😞",
      "😔",
      "😟",
      "😕",
      "🙁",
      "☹️",
      "😣",
      "😖",
      "😫",
      "😩",
      "🥺",
      "😢",
      "😭",
      "😤",
      "😠",
      "🤬",
    ],
  },
  {
    id: "gestures",
    label: "Gestures",
    emojis: [
      "👍",
      "👎",
      "👏",
      "🙌",
      "👐",
      "🤝",
      "🙏",
      "✌️",
      "🤞",
      "🤟",
      "🤘",
      "👌",
      "🤌",
      "🤏",
      "👈",
      "👉",
      "👆",
      "👇",
      "☝️",
      "✋",
      "🤚",
      "🖐️",
      "🖖",
      "👋",
      "🤙",
      "💪",
      "🫡",
      "🫂",
    ],
  },
  {
    id: "hearts",
    label: "Hearts",
    emojis: [
      "❤️",
      "🧡",
      "💛",
      "💚",
      "💙",
      "💜",
      "🖤",
      "🤍",
      "🤎",
      "💔",
      "❣️",
      "💕",
      "💞",
      "💓",
      "💗",
      "💖",
      "💘",
      "💝",
      "✨",
      "⭐",
      "🌟",
      "💫",
      "🔥",
      "💯",
    ],
  },
  {
    id: "objects",
    label: "Objects",
    emojis: [
      "🎉",
      "🎊",
      "🎈",
      "🎁",
      "🏆",
      "🥇",
      "🎯",
      "💡",
      "📌",
      "📎",
      "🔗",
      "📝",
      "📅",
      "⏰",
      "🚀",
      "✅",
      "❌",
      "⚠️",
      "💬",
      "💭",
      "🔔",
      "🎵",
      "📸",
      "🎨",
    ],
  },
]

const MessageEmojiPicker = ({ onSelect }) => {
  const [emojiCategory, setEmojiCategory] = useState(EMOJI_CATEGORIES[0].id)
  const activeEmojiCategory =
    EMOJI_CATEGORIES.find(category => category.id === emojiCategory) ||
    EMOJI_CATEGORIES[0]

  return (
    <div
      className="project-message-modal__emoji-picker"
      role="dialog"
      aria-label="Emoji picker"
    >
      <div
        className="project-message-modal__emoji-tabs"
        role="tablist"
        aria-label="Emoji categories"
      >
        {EMOJI_CATEGORIES.map(category => (
          <button
            key={category.id}
            type="button"
            role="tab"
            aria-selected={emojiCategory === category.id}
            className={`project-message-modal__emoji-tab${
              emojiCategory === category.id
                ? " project-message-modal__emoji-tab--active"
                : ""
            }`}
            onClick={() => setEmojiCategory(category.id)}
          >
            {category.label}
          </button>
        ))}
      </div>
      <div
        className="project-message-modal__emoji-grid"
        role="listbox"
        aria-label={activeEmojiCategory.label}
      >
        {activeEmojiCategory.emojis.map(emoji => (
          <button
            key={emoji}
            type="button"
            className="project-message-modal__emoji-option"
            onClick={() => onSelect(emoji)}
            aria-label={`Insert ${emoji}`}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  )
}

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join("")

const ConversationAvatar = ({ conversation }) => {
  if (conversation?.isGroup) {
    const faces = conversation.participants?.slice(0, 4) || []
    return (
      <span className="project-messages__group-avatar" aria-hidden="true">
        {faces.map((p, i) => {
          const src = getUserAvatarUrl(p) || p?.avatar
          return src ? (
            <img
              key={p?.name || i}
              src={src}
              alt=""
              className="project-messages__group-avatar-img"
            />
          ) : (
            <span
              key={p?.name || i}
              className="project-messages__group-avatar-img project-messages__group-avatar-img--fallback"
              title={p?.name}
            >
              {getInitials(p?.name)}
            </span>
          )
        })}
      </span>
    )
  }

  return (
    <Avatar
      src={
        getUserAvatarUrl(conversation?.participants?.[0]) ||
        conversation?.participants?.[0]?.avatar
      }
      name={conversation?.title}
      size="sm"
    />
  )
}

const EmptyStateIllustration = () => (
  <div className="project-messages__empty-illustration" aria-hidden="true">
    <span className="project-messages__empty-bubble project-messages__empty-bubble--gray">
      <img src={ICONS.messageBubbleDotsGray} alt="" />
    </span>
    <span className="project-messages__empty-bubble project-messages__empty-bubble--dark">
      <img src={ICONS.messageBubbleDotsDark} alt="" />
    </span>
    <img
      src={ICONS.messageSparkle1}
      alt=""
      className="project-messages__empty-sparkle project-messages__empty-sparkle--1"
    />
    <img
      src={ICONS.messageSparkle2}
      alt=""
      className="project-messages__empty-sparkle project-messages__empty-sparkle--2"
    />
    <img
      src={ICONS.messageSparkle3}
      alt=""
      className="project-messages__empty-sparkle project-messages__empty-sparkle--3"
    />
    <img
      src={ICONS.messageSparkle4}
      alt=""
      className="project-messages__empty-sparkle project-messages__empty-sparkle--4"
    />
  </div>
)

const mapInvitationToOption = invitation => {
  const user = invitation?.user || {}
  const name = getUserFullName(user, { fallback: "Unknown" })

  const avatar = getUserAvatarUrl(user)

  const value = user.id || invitation.user_id || invitation.id

  return {
    value,
    label: name,
    avatar,
    email: user.email || "",
    invitationId: invitation.id,
    role: invitation.role || "",
  }
}

const RecipientOption = props => {
  const { data } = props
  if (data?.isAll) {
    return (
      <selectComponents.Option {...props}>
        <span className="project-message-modal__option">
          <span className="project-message-modal__to-dot" aria-hidden="true" />
          <span className="project-message-modal__option-name">All</span>
        </span>
      </selectComponents.Option>
    )
  }

  return (
    <selectComponents.Option {...props}>
      <span className="project-message-modal__option">
        <Avatar
          src={data.avatar}
          name={data.label}
          size="xs"
          className="project-message-modal__option-avatar"
        />
        <span className="project-message-modal__option-meta">
          <span className="project-message-modal__option-name">
            {data.label}
          </span>
          {data.email ? (
            <span className="project-message-modal__option-email">
              {data.email}
            </span>
          ) : null}
        </span>
      </span>
    </selectComponents.Option>
  )
}

const RecipientMultiValueLabel = props => {
  const { data } = props
  if (data?.isAll) {
    return (
      <selectComponents.MultiValueLabel {...props}>
        <span className="project-message-modal__chip">
          <span className="project-message-modal__to-dot" aria-hidden="true" />
          <span className="project-message-modal__chip-name">All</span>
        </span>
      </selectComponents.MultiValueLabel>
    )
  }

  return (
    <selectComponents.MultiValueLabel {...props}>
      <span className="project-message-modal__chip">
        <Avatar
          src={data.avatar}
          name={data.label}
          size="xs"
          className="project-message-modal__chip-avatar"
        />
        <span className="project-message-modal__chip-name">{data.label}</span>
      </span>
    </selectComponents.MultiValueLabel>
  )
}

const recipientSelectStyles = {
  control: base => ({
    ...base,
    minHeight: 28,
    border: "none",
    borderRadius: 0,
    backgroundColor: "transparent",
    boxShadow: "none",
    cursor: "text",
    "&:hover": { border: "none", backgroundColor: "transparent" },
  }),
  valueContainer: base => ({
    ...base,
    padding: 0,
    gap: 6,
    flexWrap: "wrap",
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
    color: "#0d0d0d",
    fontSize: 14,
    fontWeight: 500,
  }),
  placeholder: base => ({
    ...base,
    margin: 0,
    color: "rgba(110, 110, 110, 0.7)",
    fontSize: 14,
    fontWeight: 500,
  }),
  multiValue: base => ({
    ...base,
    margin: 0,
    backgroundColor: "#ecf0fa",
    borderRadius: 999,
    alignItems: "center",
  }),
  multiValueLabel: base => ({
    ...base,
    padding: "2px 4px 2px 2px",
    paddingLeft: 2,
  }),
  multiValueRemove: base => ({
    ...base,
    borderRadius: 999,
    color: "#6e6e6e",
    cursor: "pointer",
    ":hover": {
      backgroundColor: "rgba(155, 0, 0, 0.08)",
      color: "#9b0000",
    },
  }),
  indicatorsContainer: () => ({ display: "none" }),
  menu: base => ({
    ...base,
    marginTop: 8,
    borderRadius: 12,
    boxShadow: "0 8px 24px rgba(17, 17, 17, 0.08)",
    overflow: "hidden",
    zIndex: 10060,
  }),
  menuPortal: base => ({ ...base, zIndex: 10060 }),
  menuList: base => ({
    ...base,
    padding: 4,
    maxHeight: 220,
  }),
  option: (base, state) => ({
    ...base,
    padding: "8px 10px",
    borderRadius: 8,
    backgroundColor:
      state.isFocused || state.isSelected ? "#f2f2f4" : "transparent",
    color: "#0d0d0d",
    cursor: "pointer",
    "&:active": { backgroundColor: "#e7e7ea" },
  }),
  noOptionsMessage: base => ({
    ...base,
    fontSize: 13,
    color: "#b7b7b7",
  }),
  loadingMessage: base => ({
    ...base,
    fontSize: 13,
    color: "#b7b7b7",
  }),
}

const NewMessageModal = ({ isOpen, onClose, project, onSent, initialRecipientUserId }) => {
  const selectRef = useRef(null)
  const quillRef = useRef(null)
  const attachInputRef = useRef(null)
  const emojiPickerRef = useRef(null)
  const currentUser = useSelector(state => state.auth?.currentUser)
  const [recipientOptions, setRecipientOptions] = useState([])
  const [selectedRecipients, setSelectedRecipients] = useState([])
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false)
  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const [toolbarPanel, setToolbarPanel] = useState(null) // 'emoji' | 'format' | null
  const [uploadedMedias, setUploadedMedias] = useState([])
  const [isUploadingMedia, setIsUploadingMedia] = useState(false)
  const [recipientError, setRecipientError] = useState("")
  const [sendError, setSendError] = useState("")

  const projectId = project?.id
  const projectName = project?.title || project?.name || ""
  const projectInitials = getProjectInitials(projectName)
  const currentUserId = currentUser?.id != null ? String(currentUser.id) : ""
  const currentUserEmail = String(currentUser?.email || "")
    .trim()
    .toLowerCase()
  const isAllSelected =
    recipientOptions.length > 0 &&
    selectedRecipients.length === recipientOptions.length
  const dropdownOptions = useMemo(
    () =>
      recipientOptions.length > 0
        ? [ALL_RECIPIENT_OPTION, ...recipientOptions]
        : recipientOptions,
    [recipientOptions],
  )

  const avatarPeople = useMemo(
    () =>
      (isAllSelected ? recipientOptions : selectedRecipients).map(option => ({
        name: option.label,
        src: option.avatar,
      })),
    [isAllSelected, recipientOptions, selectedRecipients],
  )

  const notifyCount = isAllSelected
    ? recipientOptions.length
    : selectedRecipients.length

  useEffect(() => {
    if (!isOpen || !projectId) return undefined

    let cancelled = false

    const isCurrentUserInvite = invitation => {
      const user = invitation?.user || {}
      const userId = user.id != null ? String(user.id) : ""
      const inviteUserId =
        invitation?.user_id != null ? String(invitation.user_id) : ""
      const email = String(user.email || "")
        .trim()
        .toLowerCase()

      if (
        currentUserId &&
        (userId === currentUserId || inviteUserId === currentUserId)
      ) {
        return true
      }
      if (currentUserEmail && email && email === currentUserEmail) {
        return true
      }
      return false
    }

    const fetchRecipients = async () => {
      setIsLoadingRecipients(true)
      try {
        const response = await get(endpoints.getProjectInvitations, {
          params: {
            involvement: "all",
            status: "active",
            project_id: projectId,
            per_page: 100,
            "with[]": ["user"],
          },
        })

        if (cancelled) return

        if (response?.data?.success) {
          const options = (response.data.result?.data || [])
            .filter(invitation => !isCurrentUserInvite(invitation))
            .map(mapInvitationToOption)
          setRecipientOptions(options)
          setSelectedRecipients([])
        } else {
          setRecipientOptions([])
          setSelectedRecipients([])
        }
      } catch {
        if (!cancelled) {
          setRecipientOptions([])
          setSelectedRecipients([])
        }
      } finally {
        if (!cancelled) setIsLoadingRecipients(false)
      }
    }

    fetchRecipients()

    return () => {
      cancelled = true
    }
  }, [isOpen, projectId, currentUserId, currentUserEmail])

  useEffect(() => {
    if (!isOpen || !initialRecipientUserId || recipientOptions.length === 0) {
      return
    }

    const match = recipientOptions.find(
      option => String(option.value) === String(initialRecipientUserId),
    )

    if (match) {
      setSelectedRecipients([match])
    }
  }, [isOpen, initialRecipientUserId, recipientOptions])

  const formik = useFormik({
    initialValues: { subject: "", body: "" },
    validationSchema: newMessageSchema,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (!projectId) {
        setSendError("Project is required")
        setSubmitting(false)
        return
      }

      if (selectedRecipients.length === 0) {
        setRecipientError("Select at least one recipient")
        setSubmitting(false)
        return
      }

      setRecipientError("")
      setSendError("")

      const payload = {
        project_ids: [projectId],
        receiver_user_ids: selectedRecipients.map(option => option.value),
        send_to_all: isAllSelected,
        subject: values.subject.trim(),
        message: values.body,
        medias: toMessageMediaPayload(uploadedMedias),
      }

      try {
        const response = await post(endpoints.sendMessage, payload, {
          skipErrorToast: true,
        })

        if (!response?.data?.success) {
          setSendError(
            getApiErrorMessage(
              response?.data || response,
              "Failed to send message",
            ),
          )
          setSubmitting(false)
          return
        }

        resetForm()
        setSelectedRecipients([])
        setUploadedMedias([])
        setMenuIsOpen(false)
        setToolbarPanel(null)
        setRecipientError("")
        setSendError("")
        if (typeof onSent === "function") onSent()
        onClose()
      } catch (err) {
        setSendError(getApiErrorMessage(err, "Failed to send message"))
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleClose = () => {
    if (formik.isSubmitting || isUploadingMedia) return
    formik.resetForm()
    setSelectedRecipients([])
    setUploadedMedias([])
    setMenuIsOpen(false)
    setToolbarPanel(null)
    setRecipientError("")
    setSendError("")
    onClose()
  }

  const handleRecipientChange = (selected, meta) => {
    setRecipientError("")
    if (meta?.option?.isAll) {
      if (meta.action === "deselect-option") {
        setSelectedRecipients([])
        return
      }
      setSelectedRecipients(recipientOptions)
      setMenuIsOpen(false)
      return
    }

    const next = (selected || []).filter(option => !option.isAll)
    setSelectedRecipients(next)
  }

  const openRecipientMenu = () => {
    setMenuIsOpen(true)
  }

  const getQuillEditor = () => quillRef.current?.getEditor?.() || null

  const insertEmoji = emoji => {
    const quill = getQuillEditor()
    if (!quill || !emoji) return

    quill.focus()
    const range = quill.getSelection(true) || {
      index: quill.getLength(),
      length: 0,
    }
    quill.insertText(range.index, emoji)
    quill.setSelection(range.index + emoji.length)
    setToolbarPanel(null)
  }

  const applyFormatOption = option => {
    const quill = getQuillEditor()
    if (!quill || !option) return

    quill.focus()
    const range = quill.getSelection(true) || {
      index: quill.getLength(),
      length: 0,
    }
    const formats = quill.getFormat(range)

    if (option.format === "list") {
      quill.format("list", formats.list === option.value ? false : option.value)
    } else {
      quill.format(option.format, !formats[option.format])
    }

    setToolbarPanel(null)
  }

  const handleToolbarAction = (toolId, event) => {
    event?.preventDefault?.()
    event?.stopPropagation?.()

    if (toolId === "emoji") {
      setToolbarPanel(panel => (panel === "emoji" ? null : "emoji"))
      return
    }

    if (toolId === "format") {
      setToolbarPanel(panel => (panel === "format" ? null : "format"))
      return
    }

    setToolbarPanel(null)

    if (toolId === "attach") {
      if (isUploadingMedia || formik.isSubmitting) return
      attachInputRef.current?.click()
      return
    }

    const quill = getQuillEditor()
    if (!quill) return

    quill.focus()
    const range = quill.getSelection(true) || {
      index: quill.getLength(),
      length: 0,
    }

    if (toolId === "insert") {
      const url = window.prompt("Enter link URL")
      if (!url) return
      if (range.length > 0) {
        quill.format("link", url)
      } else {
        quill.insertText(range.index, url, "link", url)
        quill.setSelection(range.index + url.length)
      }
      return
    }

    if (toolId === "mention") {
      quill.insertText(range.index, "@")
      quill.setSelection(range.index + 1)
    }
  }

  const handleAttachFile = async event => {
    const files = Array.from(event.target.files || [])
    event.target.value = ""
    if (files.length === 0) return

    const invalid = files.filter(
      file => !ALLOWED_MESSAGE_MEDIA_TYPES.includes(file.type),
    )
    if (invalid.length > 0) {
      toast.error(MESSAGE_MEDIA_UNSUPPORTED_MSG, TOAST_OPTIONS)
      return
    }

    const previews = createLocalMediaPreviews(files)
    setUploadedMedias(prev => [...prev, ...previews])
    setIsUploadingMedia(true)
    try {
      const { uploaded } = await uploadMessageMediaFiles(files)
      setUploadedMedias(prev => settleMediaPreviews(prev, previews, uploaded))
    } catch (err) {
      setUploadedMedias(prev => {
        previews.forEach(revokeMediaPreview)
        const previewIds = new Set(previews.map(item => item.localId))
        return prev.filter(item => !previewIds.has(item.localId))
      })
      toastApiError(err?.payload || err, "Failed to upload media")
    } finally {
      setIsUploadingMedia(false)
    }
  }

  const removeUploadedMedia = index => {
    setUploadedMedias(prev => {
      const next = [...prev]
      revokeMediaPreview(next[index])
      next.splice(index, 1)
      return next
    })
  }

  useEffect(() => {
    if (!isOpen) {
      setToolbarPanel(null)
      setUploadedMedias(prev => {
        prev.forEach(revokeMediaPreview)
        return []
      })
      setRecipientError("")
      setSendError("")
      setIsUploadingMedia(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (!toolbarPanel) return undefined

    const handlePointerDown = event => {
      if (emojiPickerRef.current?.contains(event.target)) return
      setToolbarPanel(null)
    }

    const handleKeyDown = event => {
      if (event.key === "Escape") setToolbarPanel(null)
    }

    document.addEventListener("mousedown", handlePointerDown)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("mousedown", handlePointerDown)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [toolbarPanel])

  useEffect(() => {
    if (!menuIsOpen) return undefined
    const timer = window.setTimeout(() => {
      selectRef.current?.focus?.()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [menuIsOpen])

  const isEmojiOpen = toolbarPanel === "emoji"
  const isFormatOpen = toolbarPanel === "format"

  const showAllSummary = isAllSelected && !menuIsOpen

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      scrollable
      className="amok-modal amok-modal--message"
      contentClassName="amok-modal__content project-message-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="project-message-modal__body">
        <form
          className="project-message-modal"
          onSubmit={formik.handleSubmit}
          noValidate
        >
          <div className="project-message-modal__main">
            <div className="project-message-modal__header">
              <div className="project-message-modal__project">
                <span
                  className="project-message-modal__code"
                  aria-label={projectName || "Project"}
                >
                  {projectInitials}
                </span>
                <div className="project-message-modal__heading">
                  <h3 className="project-message-modal__title">New Message</h3>
                  <p className="project-message-modal__subtitle">
                    {projectName}
                  </p>
                </div>
              </div>
            </div>

            <div className="project-message-modal__compose">
            <div className="project-message-modal__to" id="message-to">
              <span className="project-message-modal__to-label">To</span>
              <div className="project-message-modal__to-main">
                {showAllSummary ? (
                  <>
                    <button
                      type="button"
                      className="project-message-modal__to-all"
                      onClick={openRecipientMenu}
                    >
                      <span
                        className="project-message-modal__to-dot"
                        aria-hidden="true"
                      />
                      All
                    </button>
                    <AvatarGroup
                      people={avatarPeople}
                      size="xs"
                      max={3}
                      className="project-message-modal__avatars"
                    />
                  </>
                ) : null}

                <div
                  className={`project-message-modal__recipient-select-wrap${
                    showAllSummary
                      ? " project-message-modal__recipient-select-wrap--hidden"
                      : ""
                  }`}
                >
                  <Select
                    ref={selectRef}
                    className="project-message-modal__recipient-select"
                    classNamePrefix="project-message-modal-recipient"
                    options={dropdownOptions}
                    value={
                      isAllSelected
                        ? [ALL_RECIPIENT_OPTION]
                        : selectedRecipients
                    }
                    onChange={handleRecipientChange}
                    isOptionSelected={option => {
                      if (option?.isAll) return isAllSelected
                      if (isAllSelected) return false
                      return selectedRecipients.some(
                        item => String(item.value) === String(option.value),
                      )
                    }}
                    isMulti
                    isSearchable
                    isClearable={false}
                    isLoading={isLoadingRecipients}
                    menuIsOpen={menuIsOpen}
                    onMenuOpen={() => setMenuIsOpen(true)}
                    onMenuClose={() => setMenuIsOpen(false)}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    placeholder={
                      isLoadingRecipients
                        ? "Loading collaborators..."
                        : "Search collaborators..."
                    }
                    noOptionsMessage={() => "No collaborators found"}
                    filterOption={(option, rawInput) => {
                      const query = rawInput.trim().toLowerCase()
                      if (!query) return true
                      if (option?.data?.isAll) {
                        return "all".includes(query)
                      }
                      const haystack = `${option.label} ${
                        option.data?.email || ""
                      }`.toLowerCase()
                      return haystack.includes(query)
                    }}
                    components={{
                      Option: RecipientOption,
                      MultiValueLabel: RecipientMultiValueLabel,
                      IndicatorSeparator: null,
                      DropdownIndicator: null,
                    }}
                    styles={recipientSelectStyles}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    aria-label="Message recipients"
                  />
                </div>
              </div>
              <button
                type="button"
                className="project-message-modal__add"
                aria-label="Add recipient"
                onClick={openRecipientMenu}
              >
                <img src={ICONS.plus} alt="" />
              </button>
            </div>
            {recipientError ? (
              <span className="project-message-modal__error">
                {recipientError}
              </span>
            ) : null}

            <div className="project-message-modal__field">
              <label
                className="project-message-modal__label"
                htmlFor="message-subject"
              >
                Subject
              </label>
              <input
                id="message-subject"
                name="subject"
                type="text"
                className="project-message-modal__subject-input"
                value={formik.values.subject}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter subject..."
                aria-invalid={
                  formik.touched.subject && formik.errors.subject
                    ? "true"
                    : undefined
                }
              />
              {formik.touched.subject && formik.errors.subject && (
                <span className="project-message-modal__error">
                  {formik.errors.subject}
                </span>
              )}
            </div>

            <div
              className={`project-message-modal__editor${
                (formik.touched.body && formik.errors.body) || sendError
                  ? " project-message-modal__editor--error"
                  : ""
              }`}
            >
              <ReactQuill
                ref={quillRef}
                key={isOpen ? "message-editor-open" : "message-editor-closed"}
                theme="snow"
                value={formik.values.body}
                onChange={value => {
                  if (sendError) setSendError("")
                  formik.setFieldValue("body", value)
                }}
                onBlur={() => formik.setFieldTouched("body", true)}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Type your message..."
                className="project-message-modal__quill"
              />
              {formik.touched.body && formik.errors.body && (
                <span className="project-message-modal__error">
                  {formik.errors.body}
                </span>
              )}
              {sendError ? (
                <span className="project-message-modal__error" role="alert">
                  {sendError}
                </span>
              ) : null}
            </div>

            <div className="project-message-modal__toolbar">
              <div
                className="project-message-modal__tools"
                ref={emojiPickerRef}
              >
                {MESSAGE_TOOLBAR.map(tool => {
                  const isEmojiTool = tool.id === "emoji"
                  const isFormatTool = tool.id === "format"
                  const isActive =
                    (isEmojiTool && isEmojiOpen) ||
                    (isFormatTool && isFormatOpen)

                  return (
                    <div
                      key={tool.id}
                      className="project-message-modal__tool-wrap"
                    >
                      <button
                        type="button"
                        className={`project-message-modal__tool${
                          isActive ? " project-message-modal__tool--active" : ""
                        }`}
                        aria-label={tool.label}
                        title={tool.label}
                        aria-expanded={
                          isEmojiTool || isFormatTool ? isActive : undefined
                        }
                        aria-haspopup={
                          isEmojiTool || isFormatTool ? "dialog" : undefined
                        }
                        onClick={event => handleToolbarAction(tool.id, event)}
                        disabled={
                          tool.id === "attach" &&
                          (isUploadingMedia || formik.isSubmitting)
                        }
                      >
                        <img src={tool.src} alt="" />
                      </button>

                      {isEmojiTool && isEmojiOpen ? (
                        <MessageEmojiPicker onSelect={insertEmoji} />
                      ) : null}

                      {isFormatTool && isFormatOpen ? (
                        <div
                          className="project-message-modal__format-picker"
                          role="dialog"
                          aria-label="Text formatting"
                        >
                          {FORMAT_OPTIONS.map(option => (
                            <button
                              key={option.id}
                              type="button"
                              className="project-message-modal__format-option"
                              onClick={() => applyFormatOption(option)}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  )
                })}
              </div>
              <span className="project-message-modal__notify">
                {`${notifyCount} people will be notified`}
              </span>
            </div>

            {uploadedMedias.length > 0 ? (
              <ul
                className="project-message-modal__attachments"
                aria-label="Attachments"
              >
                {uploadedMedias.map((media, index) => (
                  <li
                    key={media.localId || `${media.path || media.url}-${index}`}
                    className="project-message-modal__attachment"
                  >
                    <MessageUploadThumb
                      media={media}
                      fallback={media.type === "video" ? "🎬" : "📎"}
                    />
                    <span className="project-message-modal__attachment-name">
                      {media.name ||
                        media.path?.split("/").pop() ||
                        "Attachment"}
                    </span>
                    <button
                      type="button"
                      className="project-message-modal__attachment-remove"
                      aria-label={`Remove ${media.name || "attachment"}`}
                      onClick={() => removeUploadedMedia(index)}
                      disabled={formik.isSubmitting || media.isUploading}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            <input
              ref={attachInputRef}
              type="file"
              accept={MESSAGE_MEDIA_ACCEPT}
              multiple
              className="project-message-modal__attach-input"
              aria-hidden="true"
              tabIndex={-1}
              onChange={handleAttachFile}
            />
          </div>
          </div>

          <div className="project-message-modal__actions">
            <CustomButton
              variant="danger"
              size="md"
              type="button"
              onClick={handleClose}
              disabled={formik.isSubmitting || isUploadingMedia}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="primary"
              size="md"
              type="submit"
              loading={formik.isSubmitting}
              disabled={formik.isSubmitting || isUploadingMedia}
            >
              Send Message
            </CustomButton>
          </div>
        </form>
      </ModalBody>
    </Modal>
  )
}

const MessagesUIContext = createContext(null)

const useMessagesUI = () => {
  const value = useContext(MessagesUIContext)
  if (!value) {
    throw new Error("useMessagesUI must be used within ProjectMessages")
  }
  return value
}

const MessagesEmptyState = () => {
  const { project, setIsNewOpen } = useMessagesUI()

  return (
    <div className="project-messages__empty">
      <EmptyStateIllustration />
      <div className="project-messages__empty-copy">
        <h3 className="project-messages__empty-title">Select a conversation</h3>
        <p className="project-messages__empty-subtitle">
          Choose a conversation from the list to view messages
          {project?.is_current_user_active_member ? (
            <>
              <br />
              or start a new one.
            </>
          ) : null}
        </p>
      </div>
      {project?.is_current_user_active_member && (
        <CustomButton
          variant="primary"
          size="md"
          trailingIconSrc={ICONS.plus}
          trailingIconAlt=""
          onClick={() => setIsNewOpen(true)}
        >
          New Message
        </CustomButton>
      )}
    </div>
  )
}

const formatConversationPreview = conversation => {
  const preview = conversation?.lastMessage?.text || conversation?.subject || ""
  const senderName = conversation?.lastMessage?.sender
  if (!isUsableDisplayName(senderName) || !preview) return preview
  const senderFirst = String(senderName).split(" ").filter(Boolean)[0]
  if (
    senderFirst &&
    !preview.toLowerCase().startsWith(`${senderFirst.toLowerCase()}:`)
  ) {
    return `${senderFirst}: ${preview}`
  }
  return preview
}

const MessageBody = ({
  message,
  isEditing = false,
  editValue = "",
  onEditChange,
  onEditSave,
  onEditCancel,
  isSavingEdit = false,
  editError = "",
  editInputRef,
}) => {
  if (isEditing) {
    return (
      <div className="project-messages__edit">
        <input
          ref={editInputRef}
          type="text"
          className={`project-messages__edit-input${
            editError ? " project-messages__edit-input--error" : ""
          }`}
          value={editValue}
          onChange={event => onEditChange?.(event.target.value)}
          onKeyDown={event => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault()
              onEditSave?.()
            }
            if (event.key === "Escape") {
              event.preventDefault()
              onEditCancel?.()
            }
          }}
          disabled={isSavingEdit}
          aria-label="Edit message"
        />
        {editError ? (
          <p className="project-messages__edit-error" role="alert">
            {editError}
          </p>
        ) : null}
        <div className="project-messages__edit-actions">
          <CustomButton
            variant="secondary"
            size="sm"
            type="button"
            onClick={onEditCancel}
            disabled={isSavingEdit}
          >
            Cancel
          </CustomButton>
          <CustomButton
            variant="primary"
            size="sm"
            type="button"
            onClick={onEditSave}
            loading={isSavingEdit}
            disabled={isSavingEdit || !String(editValue || "").trim()}
          >
            Save
          </CustomButton>
        </div>
      </div>
    )
  }

  if (Array.isArray(message?.paragraphs) && message.paragraphs.length > 0) {
    return (
      <div className="project-messages__message-text-block">
        {message.paragraphs.map((paragraph, index) => (
          <p
            key={`${message.id || "p"}-${index}`}
            className="project-messages__message-text"
          >
            {paragraph}
          </p>
        ))}
      </div>
    )
  }

  if (message?.html) {
    return (
      <div className="project-messages__message-html">
        {renderMessageHtml(message.html)}
      </div>
    )
  }

  return <p className="project-messages__message-text">{message?.text || ""}</p>
}

const MessageActionsMenu = ({
  isOpen,
  onToggle,
  onEdit,
  onDelete,
  disabled = false,
  triggerLabel = "Message actions",
}) => {
  const menuRef = useRef(null)

  useEffect(() => {
    if (!isOpen) return undefined

    const handlePointerDown = event => {
      if (menuRef.current?.contains(event.target)) return
      onToggle(false)
    }

    const handleKeyDown = event => {
      if (event.key === "Escape") onToggle(false)
    }

    document.addEventListener("mousedown", handlePointerDown)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("mousedown", handlePointerDown)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [isOpen, onToggle])

  return (
    <div className="project-messages__msg-menu" ref={menuRef}>
      <button
        type="button"
        className={`project-messages__msg-menu-trigger${
          isOpen ? " project-messages__msg-menu-trigger--open" : ""
        }`}
        aria-label={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        disabled={disabled}
        onClick={event => {
          event.stopPropagation()
          onToggle(!isOpen)
        }}
      >
        <span className="project-messages__msg-menu-dots" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      </button>
      {isOpen ? (
        <div className="project-messages__msg-menu-dropdown" role="menu">
          {typeof onEdit === "function" ? (
            <button
              type="button"
              className="project-messages__msg-menu-item"
              role="menuitem"
              onClick={event => {
                event.stopPropagation()
                onToggle(false)
                onEdit()
              }}
            >
              <img src={ICONS.edit} alt="" width={14} height={14} />
              <span>Edit</span>
            </button>
          ) : null}
          {typeof onDelete === "function" ? (
            <button
              type="button"
              className="project-messages__msg-menu-item project-messages__msg-menu-item--danger"
              role="menuitem"
              onClick={event => {
                event.stopPropagation()
                onToggle(false)
                onDelete()
              }}
            >
              <img src={ICONS.trash} alt="" width={14} height={14} />
              <span>Delete</span>
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

const ConversationListItem = React.memo(
  ({
    conversation,
    isActive,
    onSelect,
    isMenuOpen = false,
    onToggleMenu,
    onDelete,
    isDeleting = false,
    canManageConversations = false,
  }) => (
    <div
      className={`project-messages__item${
        isActive ? " project-messages__item--active" : ""
      }`}
      role="listitem"
    >
      <button
        type="button"
        className="project-messages__item-main"
        onClick={() => onSelect(conversation.id)}
      >
        <div className="project-messages__item-avatar">
          <ConversationAvatar conversation={conversation} />
        </div>
        <div className="project-messages__item-body">
          <span className="project-messages__item-title">
            {conversation.subject && conversation.subject !== conversation.title
              ? conversation.subject
              : conversation.title}
          </span>
          <span className="project-messages__item-preview">
            {formatConversationPreview(conversation)}
          </span>
        </div>
        <span className="project-messages__item-time">
          {conversation.lastMessage?.time || ""}
        </span>
      </button>
      {canManageConversations ? (
        <MessageActionsMenu
          isOpen={isMenuOpen}
          onToggle={open => onToggleMenu?.(open ? conversation.id : null)}
          onDelete={() => onDelete?.(conversation)}
          disabled={isDeleting}
          triggerLabel="Conversation actions"
        />
      ) : null}
    </div>
  ),
)
ConversationListItem.displayName = "ConversationListItem"

const GALLERY_PREVIEW_LIMIT = 4
const GALLERY_OVERLAY_MIN = 5

const isGalleryMedia = item =>
  Boolean(
    item?.url &&
    (item.isImage ||
      item.isVideo ||
      item.type === "IMAGE" ||
      item.type === "VIDEO"),
  )

const getGalleryLayout = count => {
  if (count <= 1) return 1
  if (count === 2) return 2
  if (count === 3) return 3
  return 4
}

const MessageGalleryTile = ({ item, extraCount = 0, onOpen }) => (
  <button
    type="button"
    className={`project-messages__media-tile${
      item.isVideo ? " project-messages__media-tile--video" : ""
    }${extraCount > 0 ? " project-messages__media-tile--more" : ""}`}
    onClick={onOpen}
    aria-label={
      extraCount > 0
        ? `View ${extraCount} more media`
        : `View ${item.name || (item.isVideo ? "video" : "image")}`
    }
  >
    {item.isVideo ? (
      <video
        src={item.url}
        muted
        playsInline
        preload="metadata"
        className="project-messages__media-tile-media"
      />
    ) : (
      <img
        src={item.url}
        alt={item.name || "Attachment"}
        className="project-messages__media-tile-media"
        draggable={false}
      />
    )}
    {item.isVideo && extraCount <= 0 ? (
      <span className="project-messages__media-tile-play" aria-hidden="true">
        <img src={ICONS.playWhite} alt="" />
      </span>
    ) : null}
    {extraCount > 0 ? (
      <span className="project-messages__media-tile-more">+{extraCount}</span>
    ) : null}
  </button>
)

const MessageAttachments = ({ message, onOpenMedia }) => {
  const items =
    Array.isArray(message?.attachments) && message.attachments.length > 0
      ? message.attachments
      : message?.attachment
        ? [message.attachment]
        : []

  if (items.length === 0) return null

  const galleryItems = items.filter(isGalleryMedia)
  const fileItems = items.filter(item => !isGalleryMedia(item))
  const showOverlay = galleryItems.length >= GALLERY_OVERLAY_MIN
  const visibleGallery = showOverlay
    ? galleryItems.slice(0, GALLERY_PREVIEW_LIMIT)
    : galleryItems
  const extraCount = showOverlay
    ? galleryItems.length - GALLERY_PREVIEW_LIMIT
    : 0
  const layout = getGalleryLayout(visibleGallery.length)

  const openMedia = index => {
    if (typeof onOpenMedia === "function") {
      onOpenMedia(galleryItems, index)
    }
  }

  return (
    <div className="project-messages__attachments">
      {galleryItems.length > 0 ? (
        <div
          className={`project-messages__media-gallery project-messages__media-gallery--${layout}`}
        >
          {visibleGallery.map((item, index) => (
            <MessageGalleryTile
              key={item.id || `${item.name || "media"}-${index}`}
              item={item}
              extraCount={
                extraCount > 0 && index === visibleGallery.length - 1
                  ? extraCount
                  : 0
              }
              onOpen={() => openMedia(index)}
            />
          ))}
        </div>
      ) : null}

      {fileItems.map((attachment, index) => (
        <div
          key={attachment.id || `${attachment.name || "file"}-${index}`}
          className="project-messages__attachment"
        >
          <div className="project-messages__attachment-left">
            <div className="project-messages__attachment-icon">
              {attachment.type || "FILE"}
            </div>
            <div className="project-messages__attachment-body">
              <span className="project-messages__attachment-name">
                {attachment.name}
              </span>
              {attachment.size ? (
                <span className="project-messages__attachment-size">
                  {attachment.size}
                </span>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className="project-messages__download"
            onClick={() => {
              if (attachment?.url) {
                window.open(attachment.url, "_blank", "noopener,noreferrer")
              }
            }}
          >
            <img src={ICONS.messageDownload} alt="" />
            Download
          </button>
        </div>
      ))}
    </div>
  )
}

const MessageMediaViewer = ({ items, index, onClose, onIndexChange }) => {
  const closeRef = useRef(null)
  const current = items[index] || null
  const hasPrev = index > 0
  const hasNext = index < items.length - 1

  useEffect(() => {
    closeRef.current?.focus()
  }, [])

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === "Escape") {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key === "ArrowLeft" && hasPrev) {
        event.preventDefault()
        onIndexChange(index - 1)
      }
      if (event.key === "ArrowRight" && hasNext) {
        event.preventDefault()
        onIndexChange(index + 1)
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [hasNext, hasPrev, index, onClose, onIndexChange])

  if (!current) return null

  const stopBubble = event => event.stopPropagation()

  return (
    <div
      className="project-messages__media-viewer"
      role="dialog"
      aria-modal="true"
      aria-label="Media viewer"
      onClick={onClose}
    >
      <button
        ref={closeRef}
        type="button"
        className="project-messages__media-viewer-close"
        aria-label="Close media viewer"
        onClick={event => {
          stopBubble(event)
          onClose()
        }}
      >
        ×
      </button>

      {hasPrev ? (
        <button
          type="button"
          className="project-messages__media-viewer-nav project-messages__media-viewer-nav--prev"
          aria-label="Previous media"
          onClick={event => {
            stopBubble(event)
            onIndexChange(index - 1)
          }}
        >
          <img src={ICONS.chevronLeft} alt="" />
        </button>
      ) : null}

      {hasNext ? (
        <button
          type="button"
          className="project-messages__media-viewer-nav project-messages__media-viewer-nav--next"
          aria-label="Next media"
          onClick={event => {
            stopBubble(event)
            onIndexChange(index + 1)
          }}
        >
          <img src={ICONS.chevronRight} alt="" />
        </button>
      ) : null}

      <div
        className="project-messages__media-viewer-stage"
        onClick={stopBubble}
      >
        {current.isVideo ? (
          <video
            key={current.id || current.url || index}
            src={current.url}
            controls
            autoPlay
            playsInline
            className="project-messages__media-viewer-media"
          />
        ) : (
          <img
            key={current.id || current.url || index}
            src={current.url}
            alt={current.name || "Attachment"}
            className="project-messages__media-viewer-media"
            draggable={false}
          />
        )}
      </div>

      <p className="project-messages__media-viewer-meta" onClick={stopBubble}>
        {current.name ? <span>{current.name}</span> : null}
        <span>
          {index + 1} / {items.length}
        </span>
      </p>
    </div>
  )
}

const MessageReactionsBar = ({
  message,
  isPickerOpen,
  onTogglePicker,
  onSelectReaction,
  isSubmitting,
  canReact = false,
}) => {
  const pickerRef = useRef(null)

  useEffect(() => {
    if (!isPickerOpen) return undefined

    const handlePointerDown = event => {
      if (pickerRef.current?.contains(event.target)) return
      onTogglePicker(false)
    }

    const handleKeyDown = event => {
      if (event.key === "Escape") onTogglePicker(false)
    }

    document.addEventListener("mousedown", handlePointerDown)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("mousedown", handlePointerDown)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [isPickerOpen, onTogglePicker])

  return (
    <div className="project-messages__reactions" ref={pickerRef}>
      {message.reactions?.map(r => (
        <button
          key={`${r.emoji}-${r.count}`}
          type="button"
          className={`project-messages__reaction${
            message.myReaction === r.emoji
              ? " project-messages__reaction--mine"
              : ""
          }`}
          onClick={canReact ? () => onSelectReaction(r.emoji) : undefined}
          disabled={!canReact || isSubmitting}
          aria-label={`React with ${r.emoji}`}
          aria-pressed={message.myReaction === r.emoji}
        >
          <span className="project-messages__reaction-emoji">{r.emoji}</span>
          <span className="project-messages__reaction-count">{r.count}</span>
        </button>
      ))}

      <div className="project-messages__react-wrap">
        {isPickerOpen && canReact ? (
          <div
            className="project-messages__react-picker"
            role="listbox"
            aria-label="Choose a reaction"
          >
            {QUICK_REACTIONS.map(emoji => (
              <button
                key={emoji}
                type="button"
                className={`project-messages__react-picker-btn${
                  message.myReaction === emoji
                    ? " project-messages__react-picker-btn--active"
                    : ""
                }`}
                onClick={() => onSelectReaction(emoji)}
                disabled={isSubmitting}
                aria-label={`React with ${emoji}`}
                aria-pressed={message.myReaction === emoji}
              >
                {emoji}
              </button>
            ))}
          </div>
        ) : null}

        {canReact && <button
          type="button"
          className={`project-messages__react-add${
            isPickerOpen ? " project-messages__react-add--open" : ""
          }`}
          onClick={() => onTogglePicker(!isPickerOpen)}
          disabled={isSubmitting}
          title="Add Reactions"
          aria-label="Add Reactions"
          aria-expanded={isPickerOpen}
          aria-haspopup="listbox"
        >
       <img
            src={ICONS.messageToolbarSmile}
            alt=""
            aria-hidden="true"
            className="project-messages__react-add-icon"
          />
        </button>}
      </div>
    </div>
  )
}

const ProjectMessageDetail = () => {
  const { contextId } = useParams()
  const {
    conversations,
    project,
    handleBackToList,
    currentUser,
    updateConversationPreview,
  } = useMessagesUI()
  const replyAttachInputRef = useRef(null)
  const replyInputRef = useRef(null)
  const replyEmojiRef = useRef(null)
  const threadScrollRef = useRef(null)
  const stickToBottomRef = useRef(true)
  const prependAdjustRef = useRef(null)
  const threadPageRef = useRef(1)
  const hasMoreOlderRef = useRef(false)
  const isLoadingOlderRef = useRef(false)
  const isThreadLoadingRef = useRef(false)
  const requestedPagesRef = useRef(new Set())
  const [reply, setReply] = useState("")
  const [replyMedias, setReplyMedias] = useState([])
  const [isUploadingReplyMedia, setIsUploadingReplyMedia] = useState(false)
  const [isSendingReply, setIsSendingReply] = useState(false)
  const [isReplyEmojiOpen, setIsReplyEmojiOpen] = useState(false)
  const [replyError, setReplyError] = useState("")
  const [mediaViewer, setMediaViewer] = useState(null)
  const [threadMessages, setThreadMessages] = useState([])
  const [threadPage, setThreadPage] = useState(1)
  const [hasMoreOlder, setHasMoreOlder] = useState(false)
  const [isThreadLoading, setIsThreadLoading] = useState(false)
  const [isLoadingOlder, setIsLoadingOlder] = useState(false)
  const [openReactionMessageId, setOpenReactionMessageId] = useState(null)
  const [reactingMessageId, setReactingMessageId] = useState(null)
  const [openMessageMenuId, setOpenMessageMenuId] = useState(null)
  const [editingMessageId, setEditingMessageId] = useState(null)
  const [editDraft, setEditDraft] = useState("")
  const [editError, setEditError] = useState("")
  const [isSavingEdit, setIsSavingEdit] = useState(false)
  const [deletingMessageId, setDeletingMessageId] = useState(null)
  const editInputRef = useRef(null)

  const currentUserId = currentUser?.id != null ? String(currentUser.id) : ""

  threadPageRef.current = threadPage
  hasMoreOlderRef.current = hasMoreOlder
  isLoadingOlderRef.current = isLoadingOlder
  isThreadLoadingRef.current = isThreadLoading

  const selected = useMemo(
    () => conversations.find(c => String(c.id) === String(contextId)) || null,
    [conversations, contextId],
  )

  const replyAvatarName =
    getUserFullName(currentUser) ||
    formatDisplayName(project?.createdBy?.name) ||
    "You"

  const collaboratorPeople = useMemo(
    () =>
      (selected?.participants || project?.collaborators || []).map(p => ({
        name: getUserFullName(p) || formatDisplayName(p?.name),
        src: getUserAvatarUrl(p) || p?.avatar,
      })),
    [selected, project?.collaborators],
  )

  const teamLabel = selected?.teamLabel || project?.title || "this project"

  const threadParticipants = useMemo(
    () => getThreadParticipants(selected, project),
    [selected, project],
  )

  const canSendReply = Boolean(reply.trim()) || replyMedias.length > 0
  const canCreateMessage = Boolean(project?.is_current_user_active_member)

  const isNearThreadBottom = useCallback((el = threadScrollRef.current) => {
    if (!el) return true
    return (
      el.scrollHeight - el.scrollTop - el.clientHeight <= THREAD_NEAR_BOTTOM_PX
    )
  }, [])

  const isNearOlderMessages = useCallback((el = threadScrollRef.current) => {
    if (!el) return false
    if (el.scrollHeight <= el.clientHeight + THREAD_TOP_LOAD_PX) return false
    return el.scrollTop <= THREAD_TOP_LOAD_PX
  }, [])

  const ingestThreadMessage = useCallback(
    (mapped, { stickIfNearBottom = true } = {}) => {
      if (!mapped?.id) return

      const enriched = enrichThreadMessage(mapped, {
        participants: threadParticipants,
        currentUser,
      })

      if (!isValidThreadMessage(enriched)) return

      if (stickIfNearBottom) {
        stickToBottomRef.current = isNearThreadBottom()
      }

      setThreadMessages(prev => mergeThreadMessages(prev, [enriched]))

      if (typeof updateConversationPreview === "function" && contextId) {
        updateConversationPreview(contextId, enriched)
      }
    },
    [
      contextId,
      currentUser,
      isNearThreadBottom,
      threadParticipants,
      updateConversationPreview,
    ],
  )

  // --- Realtime notification layer -----------------------------------
  // message.sent payloads already include the full message row
  // ({ id, body, sender, sent_at, media, reaction_summary, ... }).
  // Map those locally. Minimal notify-only events still refresh page 1.
  const isRefreshingLatestRef = useRef(false)
  const pendingLatestRefreshRef = useRef(false)
  const hadRealtimeConnectionRef = useRef(false)
  const connectionStateRef = useRef("disconnected")

  const refreshLatestMessages = useCallback(async () => {
    if (!contextId) return

    if (isRefreshingLatestRef.current) {
      pendingLatestRefreshRef.current = true
      return
    }

    isRefreshingLatestRef.current = true
    try {
      do {
        pendingLatestRefreshRef.current = false
        const stickBefore = isNearThreadBottom()

        // eslint-disable-next-line no-await-in-loop
        const response = await get(endpoints.getContextMessages(contextId), {
          params: { page: 1 },
          skipErrorToast: true,
        })

        if (!response?.data?.success) break

        const result = response.data.result || {}
        const rows = mapThreadMessagesFromApi(result.data, {
          participants: threadParticipants,
          currentUser,
          currentUserId,
        })
        if (rows.length === 0) break

        stickToBottomRef.current = stickBefore
        setThreadMessages(prev => mergeThreadMessages(prev, rows))

        const latest = rows.reduce(
          (acc, row) =>
            !acc || getMessageTimestamp(row) > getMessageTimestamp(acc)
              ? row
              : acc,
          null,
        )
        if (latest && typeof updateConversationPreview === "function") {
          updateConversationPreview(contextId, latest)
        }
      } while (pendingLatestRefreshRef.current)
    } catch {
      // Transient network error — the next Pusher notification (or the
      // user's own scroll/send) will trigger another attempt.
    } finally {
      isRefreshingLatestRef.current = false
    }
  }, [
    contextId,
    currentUser,
    currentUserId,
    isNearThreadBottom,
    threadParticipants,
    updateConversationPreview,
  ])

  const handleRealtimeNotification = useCallback(
    payload => {
      if (isContactUpdatePayload(payload)) return

      // "direct.contact.updated" events (sidebar-only, from the
      // user.direct-contacts.{userId} channel) are unrelated to whichever
      // conversation happens to be open — never let them trigger a refetch.
      if (payload && typeof payload === "object" && "contact" in payload) {
        return
      }

      const eventContextId = getRealtimeContextId(payload)
      if (eventContextId && String(eventContextId) !== String(contextId)) {
        return
      }

      const mapped = mapRealtimeMessage(payload, currentUserId, currentUser)
      if (mapped) {
        if (
          mapped.contextId &&
          String(mapped.contextId) !== String(contextId)
        ) {
          return
        }
        ingestThreadMessage(mapped)
        return
      }

      refreshLatestMessages()
    },
    [contextId, currentUser, currentUserId, ingestThreadMessage, refreshLatestMessages],
  )

  const handleReactionNotification = useCallback(
    ({ payload, messageId, reaction, action, reactionSummary, userId }) => {
      const eventContextId = getRealtimeContextId(payload)
      if (eventContextId && String(eventContextId) !== String(contextId)) {
        return
      }

      if (!messageId) {
        refreshLatestMessages()
        return
      }

      const removed = String(action || "").includes("remov")
      const actorId = String(userId || "")
      const isActor =
        Boolean(actorId && currentUserId) && actorId === String(currentUserId)
      const summaryReactions = mapReactionSummary(reactionSummary)

      setThreadMessages(prev =>
        prev.map(message => {
          if (String(message.id) !== String(messageId)) return message

          const mineMatches =
            normalizeEmoji(message.myReaction) === normalizeEmoji(reaction)
          // Remove payloads still include the emoji at the pre-remove count,
          // so never replace the listing from reaction_summary on remove.
          // Skip a second decrement when this tab already applied locally.
          const alreadyRemovedLocally = removed && isActor && !mineMatches
          let reactions = message.reactions
          if (removed) {
            if (!alreadyRemovedLocally) {
              reactions = replaceReactionCounts(
                message.reactions,
                reaction,
                null,
              )
            }
          } else if (summaryReactions != null) {
            reactions = summaryReactions
          } else {
            reactions = replaceReactionCounts(
              message.reactions,
              null,
              reaction,
            )
          }

          let myReaction = message.myReaction
          const stillListed = reactions.some(
            item => normalizeEmoji(item.emoji) === normalizeEmoji(reaction),
          )

          if (isActor) {
            if (removed) {
              myReaction = mineMatches ? "" : message.myReaction
            } else {
              myReaction = stillListed ? reaction : ""
            }
          } else if (removed && mineMatches && !stillListed) {
            myReaction = ""
          }

          return {
            ...message,
            reactions,
            myReaction,
          }
        }),
      )
    },
    [contextId, currentUserId, refreshLatestMessages],
  )

  const {
    typingLabel,
    notifyTyping,
    notifyMessageChanged,
    notifyMessageDeleted,
    notifyReaction,
    connectionState,
  } = useMessageRealtime({
    contextId,
    projectId: project?.id,
    currentUser,
    conversationRaw: selected?.raw,
    enabled: Boolean(contextId),
    onMessage: handleRealtimeNotification,
    onMessageUpdated: handleRealtimeNotification,
    onMessageDeleted: id => {
      setThreadMessages(prev =>
        prev.filter(message => String(message.id) !== String(id)),
      )
      setEditingMessageId(prev => {
        if (String(prev) !== String(id)) return prev
        setEditDraft("")
        setEditError("")
        return null
      })
      setOpenMessageMenuId(prev =>
        String(prev) === String(id) ? null : prev,
      )
    },
    onReaction: handleReactionNotification,
  })

  useEffect(() => {
    const previousState = connectionStateRef.current
    connectionStateRef.current = connectionState
    if (connectionState !== "connected") return
    if (previousState === "connected") return
    if (hadRealtimeConnectionRef.current && contextId) {
      refreshLatestMessages()
    }
    hadRealtimeConnectionRef.current = true
  }, [connectionState, contextId, refreshLatestMessages])

  const loadOlderMessages = useCallback(async () => {
    if (!contextId) return
    if (isThreadLoadingRef.current || isLoadingOlderRef.current) return
    if (!hasMoreOlderRef.current) return

    const nextPage = threadPageRef.current + 1
    if (nextPage < 2 || requestedPagesRef.current.has(nextPage)) return

    requestedPagesRef.current.add(nextPage)
    isLoadingOlderRef.current = true
    setIsLoadingOlder(true)

    const el = threadScrollRef.current
    if (el) prependAdjustRef.current = el.scrollHeight - el.scrollTop

    try {
      const response = await get(endpoints.getContextMessages(contextId), {
        params: { page: nextPage },
        skipErrorToast: true,
      })

      if (!response?.data?.success) {
        requestedPagesRef.current.delete(nextPage)
        prependAdjustRef.current = null
        return
      }

      const result = response.data.result || {}
      const rows = mapThreadMessagesFromApi(result.data, {
        participants: threadParticipants,
        currentUser,
        currentUserId,
      })
      const parsed = parseThreadPage(result, nextPage)

      if (rows.length === 0) {
        setHasMoreOlder(false)
        hasMoreOlderRef.current = false
        return
      }

      setThreadMessages(prev => mergeThreadMessages(prev, rows))
      setThreadPage(parsed.currentPage)
      threadPageRef.current = parsed.currentPage
      setHasMoreOlder(parsed.hasMoreOlder)
      hasMoreOlderRef.current = parsed.hasMoreOlder
    } catch {
      requestedPagesRef.current.delete(nextPage)
      prependAdjustRef.current = null
    } finally {
      isLoadingOlderRef.current = false
      setIsLoadingOlder(false)
    }
  }, [contextId, currentUser, currentUserId, threadParticipants])

  const handleThreadScroll = useCallback(() => {
    const el = threadScrollRef.current
    if (!el) return

    stickToBottomRef.current = isNearThreadBottom(el)

    if (
      isNearOlderMessages(el) &&
      hasMoreOlderRef.current &&
      !isLoadingOlderRef.current &&
      !isThreadLoadingRef.current
    ) {
      loadOlderMessages()
    }
  }, [isNearOlderMessages, isNearThreadBottom, loadOlderMessages])

  useEffect(() => {
    setReply("")
    setReplyMedias(prev => {
      prev.forEach(revokeMediaPreview)
      return []
    })
    setIsSendingReply(false)
    setIsUploadingReplyMedia(false)
    setIsReplyEmojiOpen(false)
    setReplyError("")
    setMediaViewer(null)
    setOpenReactionMessageId(null)
    setReactingMessageId(null)
    setOpenMessageMenuId(null)
    setEditingMessageId(null)
    setEditDraft("")
    setEditError("")
    setIsSavingEdit(false)
    setDeletingMessageId(null)
    setThreadPage(1)
    threadPageRef.current = 1
    setHasMoreOlder(false)
    hasMoreOlderRef.current = false
    setIsLoadingOlder(false)
    isLoadingOlderRef.current = false
    requestedPagesRef.current = new Set()
    stickToBottomRef.current = true
    prependAdjustRef.current = null
  }, [contextId])

  useEffect(() => {
    if (!contextId) {
      setThreadMessages([])
      setThreadPage(1)
      threadPageRef.current = 1
      setHasMoreOlder(false)
      hasMoreOlderRef.current = false
      setIsThreadLoading(false)
      isThreadLoadingRef.current = false
      return undefined
    }

    let cancelled = false
    stickToBottomRef.current = true
    prependAdjustRef.current = null
    requestedPagesRef.current = new Set([1])
    setThreadMessages([])
    setIsThreadLoading(true)
    isThreadLoadingRef.current = true

    const bootstrap = async () => {
      try {
        const response = await get(endpoints.getContextMessages(contextId), {
          params: { page: 1 },
          skipErrorToast: true,
        })

        if (cancelled) return

        if (response?.data?.success) {
          const result = response.data.result || {}
          const rows = mapThreadMessagesFromApi(result.data, {
            participants: threadParticipants,
            currentUser,
            currentUserId,
          })
          const parsed = parseThreadPage(result, 1)
          setThreadMessages(mergeThreadMessages([], rows))
          setThreadPage(parsed.currentPage)
          threadPageRef.current = parsed.currentPage
          setHasMoreOlder(parsed.hasMoreOlder)
          hasMoreOlderRef.current = parsed.hasMoreOlder
        }
      } catch {
        // Keep the empty thread on failure rather than refetching extra pages.
      } finally {
        if (!cancelled) {
          setIsThreadLoading(false)
          isThreadLoadingRef.current = false
        }
      }
    }

    bootstrap()

    return () => {
      cancelled = true
    }
  }, [contextId, currentUser, currentUserId, threadParticipants])

  useEffect(() => {
    if (!threadParticipants.length) return undefined
    setThreadMessages(prev => {
      if (!prev.length) return prev
      const enriched = prev.map(message =>
        enrichThreadMessage(message, {
          participants: threadParticipants,
          currentUser,
        }),
      )
      const changed = enriched.some(
        (message, index) =>
          message.sender !== prev[index].sender ||
          message.avatar !== prev[index].avatar,
      )
      return changed ? enriched : prev
    })
    return undefined
  }, [threadParticipants, currentUser])

  useLayoutEffect(() => {
    const el = threadScrollRef.current
    if (!el) return

    if (prependAdjustRef.current != null) {
      el.scrollTop = el.scrollHeight - prependAdjustRef.current
      prependAdjustRef.current = null
      return
    }

    if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight
    }
  }, [threadMessages])

  const handleReplyAttach = async event => {
    const files = Array.from(event.target.files || [])
    event.target.value = ""
    if (files.length === 0 || isSendingReply) return

    const invalid = files.filter(
      file => !ALLOWED_MESSAGE_MEDIA_TYPES.includes(file.type),
    )
    if (invalid.length > 0) {
      toast.error(MESSAGE_MEDIA_UNSUPPORTED_MSG, TOAST_OPTIONS)
      return
    }

    const previews = createLocalMediaPreviews(files)
    setReplyMedias(prev => [...prev, ...previews])
    setIsUploadingReplyMedia(true)
    try {
      const { uploaded } = await uploadMessageMediaFiles(files)
      setReplyMedias(prev => settleMediaPreviews(prev, previews, uploaded))
    } catch (err) {
      setReplyMedias(prev => {
        previews.forEach(revokeMediaPreview)
        const previewIds = new Set(previews.map(item => item.localId))
        return prev.filter(item => !previewIds.has(item.localId))
      })
      toastApiError(err?.payload || err, "Failed to upload media")
    } finally {
      setIsUploadingReplyMedia(false)
    }
  }

  const removeReplyMedia = index => {
    setReplyMedias(prev => {
      const next = [...prev]
      revokeMediaPreview(next[index])
      next.splice(index, 1)
      return next
    })
  }

  const handleSendReply = async () => {
    if (
      !contextId ||
      !canCreateMessage ||
      !canSendReply ||
      isSendingReply ||
      isUploadingReplyMedia
    ) {
      return
    }

    const messageText = reply.trim()
    setIsSendingReply(true)
    setReplyError("")

    try {
      const payload = {
        message: messageText,
        parent_message_id: null,
        medias: toMessageMediaPayload(replyMedias),
      }

      const response = await post(
        endpoints.postContextMessage(contextId),
        payload,
        { skipErrorToast: true },
      )

      if (!response?.data?.success) {
        setReplyError(
          getApiErrorMessage(
            response?.data || response,
            "Failed to send reply",
          ),
        )
        return
      }

      const created = extractCreatedMessage(response?.data)
      const mapped = created
        ? mapApiMessage(
            {
              ...created,
              sender: created.sender || created.user || currentUser,
              sender_id:
                created.sender_id || created.user_id || currentUser?.id,
            },
            currentUserId,
            currentUser,
          )
        : buildLocalOutgoingMessage({
            currentUser,
            text: messageText,
            medias: replyMedias,
          })

      setReply("")
      setReplyMedias([])
      setIsReplyEmojiOpen(false)
      setReplyError("")
      notifyTyping(false)
      ingestThreadMessage(mapped)
      if (created) {
        notifyMessageChanged(created)
      }
    } catch (err) {
      setReplyError(getApiErrorMessage(err, "Failed to send reply"))
    } finally {
      setIsSendingReply(false)
    }
  }

  const handleReplyKeyDown = event => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      handleSendReply()
    }
  }

  useEffect(() => {
    if (!isReplyEmojiOpen) return undefined

    const handlePointerDown = event => {
      if (replyEmojiRef.current?.contains(event.target)) return
      setIsReplyEmojiOpen(false)
    }

    const handleKeyDown = event => {
      if (event.key === "Escape") setIsReplyEmojiOpen(false)
    }

    document.addEventListener("mousedown", handlePointerDown)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("mousedown", handlePointerDown)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [isReplyEmojiOpen])

  const insertReplyEmoji = emoji => {
    if (!emoji || isSendingReply) return

    const input = replyInputRef.current
    const start =
      input && typeof input.selectionStart === "number"
        ? input.selectionStart
        : reply.length
    const end =
      input && typeof input.selectionEnd === "number"
        ? input.selectionEnd
        : reply.length
    const next = `${reply.slice(0, start)}${emoji}${reply.slice(end)}`
    setReply(next)
    setIsReplyEmojiOpen(false)

    window.requestAnimationFrame(() => {
      if (!input) return
      input.focus()
      const caret = start + emoji.length
      input.setSelectionRange(caret, caret)
    })
  }

  const applyReactionLocally = (messageId, reaction, { removed = false } = {}) => {
    setThreadMessages(prev =>
      prev.map(message => {
        if (String(message.id) !== String(messageId)) return message
        if (removed) {
          return {
            ...message,
            reactions: replaceReactionCounts(
              message.reactions,
              reaction,
              null,
            ),
            myReaction:
              normalizeEmoji(message.myReaction) === normalizeEmoji(reaction)
                ? ""
                : message.myReaction,
          }
        }
        if (message.myReaction === reaction) return message
        return {
          ...message,
          reactions: replaceReactionCounts(
            message.reactions,
            message.myReaction,
            reaction,
          ),
          myReaction: reaction,
        }
      }),
    )
  }

  const handleSelectReaction = async (messageId, reaction) => {
    if (!messageId || !reaction || reactingMessageId) return

    const current = threadMessages.find(
      message => String(message.id) === String(messageId),
    )
    const isRemoving =
      normalizeEmoji(current?.myReaction) === normalizeEmoji(reaction)

    setReactingMessageId(messageId)
    setOpenReactionMessageId(null)

    try {
      const response = isRemoving
        ? await del(endpoints.deleteMessageReaction(messageId), {
            data: { reaction },
            skipErrorToast: true,
          })
        : await post(
            endpoints.postMessageReaction(messageId),
            { reaction },
            { skipErrorToast: true },
          )

      if (!response?.data?.success) {
        toastApiError(
          response?.data || response,
          isRemoving ? "Failed to remove reaction" : "Failed to add reaction",
        )
        return
      }

      applyReactionLocally(messageId, reaction, { removed: isRemoving })
      notifyReaction({
        messageId,
        reaction,
        action: isRemoving ? "removed" : "added",
      })
    } catch (err) {
      toastApiError(
        err,
        isRemoving ? "Failed to remove reaction" : "Failed to add reaction",
      )
    } finally {
      setReactingMessageId(null)
    }
  }

  const handleStartEditMessage = useCallback(message => {
    if (!message?.id || isLocalMessageId(message.id) || !message.isMe) return
    setOpenMessageMenuId(null)
    setOpenReactionMessageId(null)
    setEditingMessageId(message.id)
    setEditDraft(message.text || stripHtml(message.html) || "")
    setEditError("")
  }, [])

  const handleCancelEditMessage = useCallback(() => {
    setEditingMessageId(null)
    setEditDraft("")
    setEditError("")
    setIsSavingEdit(false)
  }, [])

  useEffect(() => {
    if (!editingMessageId) return undefined
    const frame = window.requestAnimationFrame(() => {
      editInputRef.current?.focus?.()
      editInputRef.current?.select?.()
    })
    return () => window.cancelAnimationFrame(frame)
  }, [editingMessageId])

  const handleSaveEditMessage = useCallback(async () => {
    if (!editingMessageId || isSavingEdit) return
    if (isLocalMessageId(editingMessageId)) return

    const current = threadMessages.find(
      message => String(message.id) === String(editingMessageId),
    )
    if (!current?.isMe) return

    const nextText = String(editDraft || "").trim()
    if (!nextText) {
      setEditError("Message cannot be empty")
      return
    }

    setIsSavingEdit(true)
    setEditError("")

    try {
      const response = await put(
        endpoints.updateMessage(editingMessageId),
        {
          message: nextText,
          remove_media_ids: [],
          medias: buildUpdateMediasPayload(current),
        },
        { skipErrorToast: true },
      )

      if (!response?.data?.success) {
        setEditError(
          getApiErrorMessage(
            response?.data || response,
            "Failed to update message",
          ),
        )
        return
      }

      const updatedRaw = extractCreatedMessage(response?.data) || {
        ...current,
        id: current.id,
        message: nextText,
        body: nextText,
        sender: currentUser,
        sender_id: currentUserId,
        medias: current.medias,
        sent_at: current.sentAt,
        context_id: current.contextId || contextId,
      }

      const mapped = mapApiMessage(
        {
          ...updatedRaw,
          sender: updatedRaw.sender || updatedRaw.user || currentUser,
          sender_id:
            updatedRaw.sender_id || updatedRaw.user_id || currentUser?.id,
          medias: updatedRaw.medias || updatedRaw.media || current.medias,
          message: updatedRaw.message || updatedRaw.body || nextText,
        },
        currentUserId,
        currentUser,
      )

      if (mapped?.id) {
        ingestThreadMessage({
          ...mapped,
          medias: mapped.medias?.length ? mapped.medias : current.medias,
          attachments: mapped.attachments?.length
            ? mapped.attachments
            : current.attachments,
        })
        notifyMessageChanged(updatedRaw)
      }

      setEditingMessageId(null)
      setEditDraft("")
      setEditError("")
    } catch (err) {
      setEditError(getApiErrorMessage(err, "Failed to update message"))
    } finally {
      setIsSavingEdit(false)
    }
  }, [
    contextId,
    currentUser,
    currentUserId,
    editDraft,
    editingMessageId,
    ingestThreadMessage,
    isSavingEdit,
    notifyMessageChanged,
    threadMessages,
  ])

  const handleDeleteMessage = useCallback(
    async message => {
      if (!message?.id || !message.isMe || deletingMessageId) return
      if (isLocalMessageId(message.id)) return

      setOpenMessageMenuId(null)
      setDeletingMessageId(message.id)

      try {
        const response = await del(endpoints.deleteMessage(message.id), {
          skipErrorToast: true,
        })

        if (!response?.data?.success) {
          toastApiError(response?.data || response, "Failed to delete message")
          return
        }

        setThreadMessages(prev =>
          prev.filter(item => String(item.id) !== String(message.id)),
        )
        if (String(editingMessageId) === String(message.id)) {
          setEditingMessageId(null)
          setEditDraft("")
          setEditError("")
        }
        notifyMessageDeleted(message.id)
      } catch (err) {
        toastApiError(err, "Failed to delete message")
      } finally {
        setDeletingMessageId(null)
      }
    },
    [deletingMessageId, editingMessageId, notifyMessageDeleted],
  )

  if (!selected) {
    return (
      <div className="project-messages__empty">
        <EmptyStateIllustration />
        <div className="project-messages__empty-copy">
          <h3 className="project-messages__empty-title">
            Conversation not found
          </h3>
          <p className="project-messages__empty-subtitle">
            This conversation is not in the current list.
            <br />
            Try another conversation or refresh.
          </p>
        </div>
        <CustomButton variant="secondary" size="md" onClick={handleBackToList}>
          Back to messages
        </CustomButton>
      </div>
    )
  }

  return (
    <div
      className={`project-messages__chat${
        mediaViewer ? " project-messages__chat--media-open" : ""
      }`}
    >
      <div className="project-messages__chat-header">
        <button
          type="button"
          className="project-messages__back"
          onClick={handleBackToList}
          aria-label="Back to conversations"
        >
          <img src={ICONS.chevronLeft} alt="" />
        </button>
        <div className="project-messages__chat-title">
          <div className="project-messages__chat-heading">
            <h3>{selected.title}</h3>
            {selected.participants?.length === 1 &&
            selected.participants[0]?.id ? (
              <OnlineStatus
                userId={selected.participants[0].id}
                variant="dot"
                className="project-messages__presence"
              />
            ) : null}
          </div>
          {selected.isPrivate && (
            <span className="project-messages__chat-meta">
              Private to {teamLabel} team
            </span>
          )}
        </div>
        {/* <div className="project-messages__chat-actions">
          <CustomButton
            variant="secondary"
            size="sm"
            trailingIconSrc={ICONS.messageShare}
            trailingIconAlt=""
            className="project-messages__share-btn"
            onClick={() => {}}
          >
            Share
          </CustomButton>
        </div> */}
      </div>

      {selected?.createdByName ? (
        <div className="project-messages__created-by">
          <span className="project-messages__created-by-line" />
          <span className="project-messages__created-by-text">
            {selected.createdByName} started this conversation
          </span>
          <span className="project-messages__created-by-line" />
        </div>
      ) : null}

      <div
        id={THREAD_SCROLL_ID}
        ref={threadScrollRef}
        className="project-messages__chat-messages"
        onScroll={handleThreadScroll}
      >
        {isThreadLoading && threadMessages.length === 0 ? (
          <div className="project-messages__thread-empty" aria-busy="true">
            Loading messages...
          </div>
        ) : null}

        {!isThreadLoading && threadMessages.length === 0 ? (
          <div className="project-messages__thread-empty">
            No messages in this conversation yet.
          </div>
        ) : null}

        {isLoadingOlder ? <ThreadOlderLoader /> : null}

        {threadMessages.length > 0 ? (
          <InfiniteScroll
            dataLength={threadMessages.length}
            next={loadOlderMessages}
            hasMore={hasMoreOlder && !isThreadLoading}
            inverse
            scrollThreshold={THREAD_OLDER_SCROLL_THRESHOLD}
            loader={<ThreadOlderLoader />}
            scrollableTarget={THREAD_SCROLL_ID}
            className="project-messages__thread-list"
            style={{
              overflow: "visible",
              display: "flex",
              flexDirection: "column-reverse",
            }}
          >
            {[...threadMessages].reverse().map((m, i) => (
              <div
                key={m.id || `${m.sender}-${i}`}
                className={`project-messages__message${
                  m.isMe ? " project-messages__message--me" : ""
                }`}
              >
                {!m.isMe && (
                  <Avatar
                    src={m.avatar || getUserAvatarUrl(m)}
                    name={m?.sender}
                    size="sm"
                    className="project-messages__message-avatar"
                  />
                )}
                <div className="project-messages__message-content">
                  <div className="project-messages__message-meta">
                    {m.isMe ? (
                      <Avatar
                        src={
                          m.avatar ||
                          getUserAvatarUrl(currentUser) ||
                          getUserAvatarUrl(m)
                        }
                        name={m?.sender}
                        size="sm"
                        className="project-messages__message-avatar"
                      />
                    ) : null}
                    <span className="project-messages__message-sender">
                      {m.sender}
                    </span>
                    <span
                      className="project-messages__message-dot"
                      aria-hidden="true"
                    >
                      •
                    </span>
                    <span className="project-messages__message-time">
                      {m.time}
                    </span>
                    {m.isMe ? (
                      <span className="project-messages__message-you">You</span>
                    ) : m.role ? (
                      <span className="project-messages__message-role">
                        {m.role}
                      </span>
                    ) : null}
                    {m.isMe && !isLocalMessageId(m.id) ? (
                      <MessageActionsMenu
                        isOpen={String(openMessageMenuId) === String(m.id)}
                        onToggle={open =>
                          setOpenMessageMenuId(open ? m.id : null)
                        }
                        onEdit={() => handleStartEditMessage(m)}
                        onDelete={() => handleDeleteMessage(m)}
                        disabled={
                          isSavingEdit ||
                          String(deletingMessageId) === String(m.id)
                        }
                      />
                    ) : null}
                  </div>

                  <MessageBody
                    message={m}
                    isEditing={String(editingMessageId) === String(m.id)}
                    editValue={editDraft}
                    onEditChange={value => {
                      setEditDraft(value)
                      if (editError) setEditError("")
                    }}
                    onEditSave={handleSaveEditMessage}
                    onEditCancel={handleCancelEditMessage}
                    isSavingEdit={isSavingEdit}
                    editError={
                      String(editingMessageId) === String(m.id) ? editError : ""
                    }
                    editInputRef={
                      String(editingMessageId) === String(m.id)
                        ? editInputRef
                        : undefined
                    }
                  />

                  {(m.signOff || m.signatureName || m.signatureTitle) && (
                    <div className="project-messages__signature">
                      {m.signOff && (
                        <p className="project-messages__signature-off">
                          {m.signOff}
                        </p>
                      )}
                      {m.signatureName && (
                        <p className="project-messages__signature-name">
                          {m.signatureName}
                        </p>
                      )}
                      {m.signatureTitle && (
                        <p className="project-messages__signature-title">
                          {m.signatureTitle}
                        </p>
                      )}
                    </div>
                  )}

                  <MessageAttachments
                    message={m}
                    onOpenMedia={(items, index) =>
                      setMediaViewer({ items, index })
                    }
                  />

                  <MessageReactionsBar
                    message={m}
                    isPickerOpen={
                      String(openReactionMessageId) === String(m.id)
                    }
                    onTogglePicker={open =>
                      setOpenReactionMessageId(open ? m.id : null)
                    }
                    onSelectReaction={emoji =>
                      handleSelectReaction(m.id, emoji)
                    }
                    isSubmitting={String(reactingMessageId) === String(m.id)}
                    canReact={canCreateMessage}
                  />
                </div>
              </div>
            ))}
          </InfiniteScroll>
        ) : null}
      </div>

      <div className="project-messages__chat-footer">
        {replyMedias.length > 0 ? (
          <ul
            className="project-messages__reply-attachments"
            aria-label="Reply attachments"
          >
            {replyMedias.map((media, index) => (
              <li
                key={media.localId || `${media.path || media.url}-${index}`}
                className="project-messages__reply-attachment"
              >
                <MessageUploadThumb
                  media={media}
                  fallback={media.type === "video" ? "VID" : "FILE"}
                />
                <span className="project-messages__reply-attachment-name">
                  {media.name || media.path?.split("/").pop() || "Attachment"}
                </span>
                <button
                  type="button"
                  className="project-messages__reply-attachment-remove"
                  aria-label={`Remove ${media.name || "attachment"}`}
                  onClick={() => removeReplyMedia(index)}
                  disabled={isSendingReply || media.isUploading}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        {canCreateMessage ? (
          <div className="project-messages__chat-composer">
          <div className="project-messages__chat-input">
            <Avatar
              src={getUserAvatarUrl(currentUser) || project?.createdBy?.avatar}
              name={replyAvatarName}
              size="sm"
              className="project-messages__input-avatar"
            />
            <div
              className={`project-messages__input-wrap${
                replyError ? " project-messages__input-wrap--error" : ""
              }`}
            >
              <button
                type="button"
                className="project-messages__input-action"
                aria-label={MESSAGE_MEDIA_ATTACH_TOOLTIP}
                title={MESSAGE_MEDIA_ATTACH_TOOLTIP}
                onClick={() => replyAttachInputRef.current?.click()}
                disabled={isSendingReply || isUploadingReplyMedia || !canCreateMessage}
              >
                <img src={ICONS.messageExport} alt="" />
              </button>
              <input
                ref={replyInputRef}
                type="text"
                value={reply}
                onChange={e => {
                  const nextValue = e.target.value
                  if (replyError) setReplyError("")
                  setReply(nextValue)
                  notifyTyping(Boolean(nextValue.trim()))
                }}
                onKeyDown={handleReplyKeyDown}
                placeholder="Reply to message..."
                aria-label="Reply to message"
                aria-invalid={replyError ? "true" : undefined}
                disabled={isSendingReply || !canCreateMessage}
              />
              <div
                className="project-messages__reply-emoji-wrap"
                ref={replyEmojiRef}
              >
                {isReplyEmojiOpen ? (
                  <MessageEmojiPicker onSelect={insertReplyEmoji} />
                ) : null}
                <button
                  type="button"
                  className={`project-messages__input-action${
                    isReplyEmojiOpen
                      ? " project-messages__input-action--active"
                      : ""
                  }`}
                  aria-label="Add emoji"
                  aria-expanded={isReplyEmojiOpen}
                  aria-haspopup="dialog"
                  onClick={() => setIsReplyEmojiOpen(open => !open)}
                  disabled={isSendingReply || !canCreateMessage}
                >
                  <img src={ICONS.messageToolbarSmile} alt="" />
                </button>
              </div>
              <button
                type="button"
                className="project-messages__input-action project-messages__input-action--send"
                aria-label="Send reply"
                onClick={handleSendReply}
                disabled={
                  !canSendReply ||
                  !canCreateMessage ||
                  isSendingReply ||
                  isUploadingReplyMedia
                }
              >
                <img src={ICONS.send} alt="" />
              </button>
            </div>
            <input
              ref={replyAttachInputRef}
              type="file"
              accept={MESSAGE_MEDIA_ACCEPT}
              multiple
              className="project-messages__reply-attach-input"
              aria-hidden="true"
              tabIndex={-1}
              onChange={handleReplyAttach}
            />
          </div>
          {typingLabel ? (
            <p className="project-messages__typing" aria-live="polite">
              {typingLabel}
            </p>
          ) : null}
          {replyError ? (
            <p className="project-messages__reply-error" role="alert">
              {replyError}
            </p>
          ) : null}
        </div>
        ) : null}

        <div className="project-messages__collaborators">
          <span className="project-messages__collaborators-label">
            Collaborators
          </span>
          <AvatarGroup people={collaboratorPeople} size="xs" max={4} />
        </div>
      </div>

      {mediaViewer?.items?.length > 0 ? (
        <MessageMediaViewer
          items={mediaViewer.items}
          index={mediaViewer.index}
          onClose={() => setMediaViewer(null)}
          onIndexChange={nextIndex =>
            setMediaViewer(prev =>
              prev ? { ...prev, index: nextIndex } : prev,
            )
          }
        />
      ) : null}
    </div>
  )
}

const ProjectMessages = ({
  project,
  isNewOpen: controlledNewOpen,
  onNewOpenChange,
  initialRecipientUserId = null,
}) => {
  const navigate = useNavigate()
  const { projectId: routeProjectId, "*": messagesSplat } = useParams()
  const [conversations, setConversations] = useState([])
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [internalNewOpen, setInternalNewOpen] = useState(false)
  const [openConversationMenuId, setOpenConversationMenuId] = useState(null)
  const [deletingContextId, setDeletingContextId] = useState(null)
  const currentUser = useSelector(state => state.auth?.currentUser)
  const currentUserId = currentUser?.id != null ? String(currentUser.id) : ""
  const currentUserType = useSelector(state => state.auth?.currentUser?.type)
  const projectId = project?.id || routeProjectId
  // Under messages/*, the selected conversation is the first splat segment.
  const selectedId =
    String(messagesSplat || "")
      .split("/")
      .filter(Boolean)[0] || null

  const isNewOpen =
    typeof controlledNewOpen === "boolean" ? controlledNewOpen : internalNewOpen

  const setIsNewOpen = useCallback(
    open => {
      if (onNewOpenChange) {
        onNewOpenChange(open)
      } else {
        setInternalNewOpen(open)
      }
    },
    [onNewOpenChange]
  )

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
    }, SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [search])

  const fetchContexts = useCallback(
    async ({
      page = 1,
      append = false,
      searchQuery = debouncedSearch,
    } = {}) => {
      if (!projectId) {
        setConversations([])
        setHasMore(false)
        setIsLoading(false)
        setIsLoadingMore(false)
        return
      }

      if (append) setIsLoadingMore(true)
      else setIsLoading(true)

      try {
        const response = await get(endpoints.getMessageContexts, {
          params: {
            project_id: projectId,
            search: searchQuery || "",
            page,
          },
          skipErrorToast: true,
        })

        if (!response?.data?.success) {
          if (!append) {
            setConversations([])
          }
          setHasMore(false)
          return
        }

        const result = response.data.result || {}
        const rows = (result.data || []).map(item =>
          mapContextToConversation(item, currentUserId, currentUser),
        )
        const lastPage = Number(result.last_page) || 1
        const pageNumber = Number(result.current_page) || page

        setConversations(prev => (append ? [...prev, ...rows] : rows))
        setCurrentPage(pageNumber)
        setHasMore(pageNumber < lastPage)
      } catch {
        if (!append) {
          setConversations([])
        }
        setHasMore(false)
      } finally {
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    [projectId, debouncedSearch, currentUser, currentUserId],
  )

  useEffect(() => {
    setCurrentPage(1)
    fetchContexts({ page: 1, append: false, searchQuery: debouncedSearch })
  }, [fetchContexts, debouncedSearch])

  useEffect(() => {
    let cancelled = false

    const resolveSelectedConversation = async () => {
      if (!selectedId || !projectId) return

      if (conversations.some(c => String(c.id) === String(selectedId))) return

      const loadedMatch = findConversationForUser(
        conversations,
        selectedId,
        currentUserId,
      )
      if (loadedMatch) {
        navigate(`${loadedMatch.id}`, { relative: "path", replace: true })
        return
      }

      if (isLoading) return

      const contextId = await findProjectMessageContextId(
        projectId,
        selectedId,
        currentUserId,
      )
      if (cancelled) return

      if (contextId && String(contextId) !== String(selectedId)) {
        navigate(`${contextId}`, { relative: "path", replace: true })
      }
    }

    resolveSelectedConversation()

    return () => {
      cancelled = true
    }
  }, [
    selectedId,
    conversations,
    isLoading,
    projectId,
    navigate,
    currentUserId,
  ])

  const loadMoreContexts = () => {
    if (isLoading || isLoadingMore || !hasMore) return
    fetchContexts({
      page: currentPage + 1,
      append: true,
      searchQuery: debouncedSearch,
    })
  }

  const refreshContexts = useCallback(() => {
    fetchContexts({ page: 1, append: false, searchQuery: debouncedSearch })
  }, [fetchContexts, debouncedSearch])

  const updateConversationPreview = useCallback((id, message) => {
    if (!id || !message) return

    setConversations(prev => {
      const index = prev.findIndex(item => String(item.id) === String(id))
      if (index < 0) return prev

      const current = prev[index]
      const senderFromMessage = isUsableDisplayName(message.sender)
        ? message.sender
        : ""
      const senderFromParticipants = resolveParticipantName(
        current.participants,
        message.senderId,
      )
      const sender =
        senderFromMessage ||
        senderFromParticipants ||
        (isUsableDisplayName(current.lastMessage?.sender)
          ? current.lastMessage.sender
          : null)

      const updated = {
        ...current,
        lastMessage: {
          text:
            message.text ||
            (message.attachments?.length ? "Attachment" : "") ||
            current.lastMessage?.text ||
            "",
          time: message.time || current.lastMessage?.time || "",
          sender,
        },
        messages: mergeThreadMessages(
          current.messages || [],
          isValidThreadMessage(message) ? [message] : [],
        ),
      }

      if (index === 0) {
        const next = [...prev]
        next[0] = updated
        return next
      }

      return [updated, ...prev.filter((_, itemIndex) => itemIndex !== index)]
    })
  }, [])

  const handleDirectContactUpdate = useCallback(
    payload => {
      const preview = mapDirectContactPreview(payload)
      if (!preview) return
      updateConversationPreview(preview.contextId, preview)
    },
    [updateConversationPreview],
  )

  useMessageRealtime({
    projectId,
    currentUser,
    enabled: Boolean(projectId),
    // Sidebar preview: updated locally only, never via a contexts-list
    // refetch. Channel/event contract confirmed against the backend's own
    // Pusher test harness: user.direct-contacts.{userId} + direct.contact.updated.
    // The event name contains "updated", so the generic classifier in
    // useMessageRealtime routes it through onMessageUpdated.
    onMessage: handleDirectContactUpdate,
    onMessageUpdated: handleDirectContactUpdate,
  })

  const handleSelect = useCallback(
    id => {
      if (!projectId || !id) return
      const link = currentUserType === 'member' ? `/projects/${projectId}/messages/${id}` : `/admin/projects/${projectId}/messages/${id}`
      navigate(link)
    },
    [navigate, projectId, currentUserType],
  )

  const handleBackToList = useCallback(() => {
    if (!projectId) return
    const link = currentUserType === 'member' ? `/projects/${projectId}/messages` : `/admin/projects/${projectId}/messages`
    navigate(link)
  }, [navigate, projectId, currentUserType])

  const handleDeleteConversation = useCallback(
    async conversation => {
      const contextId = conversation?.id
      if (!contextId || deletingContextId) return

      setOpenConversationMenuId(null)
      setDeletingContextId(String(contextId))

      try {
        const response = await del(
          endpoints.deleteMessageContext(contextId),
          { skipErrorToast: true },
        )

        if (!response?.data?.success) {
          toastApiError(
            response?.data || response,
            "Failed to delete conversation",
          )
          return
        }

        setConversations(prev =>
          prev.filter(item => String(item.id) !== String(contextId)),
        )

        window.dispatchEvent(
          new CustomEvent("direct-contact-deleted", { detail: { id: contextId } }),
        )

        if (String(selectedId) === String(contextId)) {
          handleBackToList()
        }

        toast.success("Conversation deleted.", TOAST_OPTIONS)
      } catch (err) {
        toastApiError(err, "Failed to delete conversation")
      } finally {
        setDeletingContextId(null)
      }
    },
    [deletingContextId, handleBackToList, selectedId],
  )

  const uiValue = useMemo(
    () => ({
      project,
      conversations,
      currentUser,
      setIsNewOpen,
      handleBackToList,
      refreshContexts,
      updateConversationPreview,
    }),
    [
      project,
      conversations,
      currentUser,
      setIsNewOpen,
      handleBackToList,
      refreshContexts,
      updateConversationPreview,
    ],
  )

  return (
    <MessagesUIContext.Provider value={uiValue}>
      <div
        className={`project-messages${
          selectedId ? " project-messages--chat-open" : ""
        }`}
      >
        <div className="project-messages__list">
          <div className="project-messages__search">
            <img src={ICONS.search} alt="" />
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search Conversation"
              aria-label="Search Conversation"
            />
          </div>

          <div
            id="project-messages-conversations"
            className="project-messages__conversations"
            role="list"
          >
            <InfiniteScroll
              dataLength={conversations.length}
              next={loadMoreContexts}
              hasMore={hasMore}
              loader={
                <div
                  className="project-messages__scroll-status"
                  aria-busy="true"
                >
                  Loading...
                </div>
              }
              scrollableTarget="project-messages-conversations"
              style={{
                overflow: "visible",
                display: "flex",
                flexDirection: "column",
                gap: "inherit",
              }}
            >
              {!isLoading &&
                conversations.map(c => (
                  <ConversationListItem
                    key={c.id}
                    conversation={c}
                    isActive={c.id === selectedId}
                    onSelect={handleSelect}
                    isMenuOpen={
                      String(openConversationMenuId) === String(c.id)
                    }
                    onToggleMenu={setOpenConversationMenuId}
                    onDelete={handleDeleteConversation}
                    isDeleting={
                      String(deletingContextId) === String(c.id)
                    }
                    canManageConversations={Boolean(
                      project?.is_current_user_active_member,
                    )}
                  />
                ))}
            </InfiniteScroll>

            {isLoading ? (
              <div className="project-messages__scroll-status" aria-busy="true">
                Loading...
              </div>
            ) : null}

            {!isLoading && conversations.length === 0 ? (
              <div className="project-messages__scroll-status">
                No conversations found
              </div>
            ) : null}
          </div>
        </div>

        <Routes>
          <Route index element={<MessagesEmptyState />} />
          <Route path=":contextId" element={<ProjectMessageDetail />} />
        </Routes>
      </div>

      <NewMessageModal
        isOpen={isNewOpen}
        onClose={() => setIsNewOpen(false)}
        project={project}
        onSent={refreshContexts}
        initialRecipientUserId={initialRecipientUserId}
      />
    </MessagesUIContext.Provider>
  )
}

export default ProjectMessages
